/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef, createContext, useContext, useMemo } from "react";
import { API_URL, API_SECRET, PING_URL, MAPS_KEY_URL, NOTIFICATIONS_DEVICE_URL, USER_MODULE_USAGE_URL, BOOT_TELEMETRY_URL, DEVICE_REGISTRY_URL, MAPS_PLATFORM, GLOBAL_COLLECTIONS, GLOBAL_BOOTSTRAP_PRIORITY, GLOBAL_BOOTSTRAP_SECONDARY, GLOBAL_SYNC_FRESHNESS_MS, GLOBAL_KEY, isNativeShell } from "./config/appConfig";
import { PRELOGIN_STAGE_LABELS, getInitialLoginReady, getInitialPreloginProgress, getInitialPreloginStage } from "./bootstrap/startupPhases";
import { BOOT_COUNTDOWN_SECONDS } from "./config/bootConfig";
import { createAppContextValue } from "./context/AppContext";
import AppShell from "./app/AppShell";
import AppRoutes from "./app/AppRoutes";
import { useAppPreferences } from "./app/appPreferencesController";
import { resetMobileViewportAfterLogin, useBeforeUnloadGuard, useSessionViewportReset } from "./app/appNavigationController";
import { useStoredSessionState, useStoredActiveFarmState, useMaintenanceInfo, createHandleLogin, createHandleLogout, useSolicitanteFarmGuard } from "./app/appSessionController";
import { buildLoginScreenDeps, buildFarmDashboardDeps, buildSyncProviderDeps, buildGestorPanelDeps } from "./app/appDeps";
import { buildAdminDeps } from "./admin/adminDeps";
import { FARM_COLLECTIONS, FARM_BUNDLE_GROUPS } from "./admin/adminData";
import { FARM_GENERATION_OPERATIONAL_COLLECTIONS, getActiveFarmGeneration } from "./lib/farmGenerations";
import { fetchServerVersion, requestWarmScope, requestHomeWarmCache, requestShellWarmCache, requestPriorityWarmCache, requestAdaptiveWarmCache, registerBootServiceWorker } from "./bootstrap/swRuntime";
import { allowTechnicalReloadWindow, buildUpdateUrl, softReloadToLatest as performSoftReloadToLatest, waitForNextController, waitForServiceWorkerActivationAck } from "./bootstrap/updateFlow";
import { getDeviceId, DB } from "./lib/deviceStorage";
import { safeLocalStorageSetJson, safeLocalStorageSetItem } from "./lib/storageQuotaRecovery";
import { restoreSessionFromIdbIfNeeded, readStoredSession, readStoredActiveFarm } from "./lib/sessionUtils";
import { fmtDate, fmtDateTime, fmtMoney, fmtMeasure, today, buildOrderTitle, nextId, createClientId, compressImage } from "./lib/formatters";
import { FeedBagIcon, GenericRationBagIcon, BovineRationBagIcon, SheepHeadIcon, BovineHeadIcon, HorseIcon, CattleIcon } from "./lib/icons";
import { SYSTEM_LISTS_DEFAULTS, T, COST_CENTERS, CC_ICONS, CC_COLORS, DEFAULT_SKIP_REASONS, MACH_TYPES, FUEL_TYPES, ENGINE_TYPES, FUEL_KEYWORDS, isCompactViewport, calcStockQtyFromMovements, enrichStockItemsWithQty, FARM_COLORS, FARM_EMOJIS, ANIMAL_SPECIES, DEFAULT_VISIBLE_FARM_SPECIES, COLLAB_LEVELS } from "./lib/globals";
import { makeSeed } from "./lib/seed";
import { makeCreateMeta, makeUpdateMeta } from "./lib/meta";
import { classifyConflictSeverity } from "./lib/conflictRules";
import { listCurrentEditingPresence } from "./lib/presence";
import { SyncQueue } from "./sync/syncQueue";
import { verKey, shadowKey, opsKey, pendingKey, parseSyncTs, itemSyncTs, isDeletedItem, stripDeletedItems, stableJson, getChangedFields, buildOperationLog, applyOperationLog, mergeFieldValue, buildLocalRawForSync, mergeCollectionsThreeWay, appendSyncHistory, isAppendOnlyCollection } from "./lib/syncUtils";
import { createMachineryModule } from "./modules/createMachineryModule";
import { createLocationModule } from "./modules/createLocationModule";
import { createHarasModule } from "./modules/createHarasModule";
import { createAnimalsModule } from "./modules/createAnimalsModule";
import { createFinancialModule } from "./modules/createFinancialModule";
import { createInspectionsModule } from "./modules/createInspectionsModule";
import { normalizeAdminNotificationSettings, isAdminNotificationEnabled } from "./lib/adminNotificationPrefs";
import { createServicesModule } from "./modules/createServicesModule";
import { createOccurrencesModule } from "./modules/createOccurrencesModule";
import { createPurchasesModule } from "./modules/createPurchasesModule";
import { createStockModule } from "./modules/createStockModule";
import { createHRModule } from "./modules/createHRModule";
import ModuleHeaderMenu from "./components/ModuleHeaderMenu";
import LoginScreen from "./components/LoginScreen";
import GestorPanel from "./components/GestorPanel";
import FarmDashboard from "./components/FarmDashboard";
import { ensureBootTelemetryState, markBootTelemetry, setBootTelemetryIdentity, finalizeBootTelemetry } from "./bootstrap/bootTelemetry";
import { flushBootTelemetryToServer } from "./bootstrap/bootTelemetryReporter";
import { createPreSyncLogin } from "./bootstrap/prelogin";
import { compareVersions, useVersionCheck, VersionUpdateOverlay } from "./bootstrap/versionCheck";
import { syncGlobalCollections } from "./sync/syncGlobal";
import { useSync, useSysLists } from "./sync/SyncProvider";
import { endAllLiveActivitiesForFarm, getPendingLiveActivityDeepLink, clearPendingLiveActivityDeepLink, subscribeLiveActivityDeepLink, reportLiveTouchDiagnosticToServer } from "./lib/liveActivities";
import { WARM_USAGE_STORAGE_KEY, MODULE_PRIORITY_WARM_PLAN, buildFarmWarmUsageKey, buildHomeWarmUsageKey, buildWarmModuleSequence, flushWarmUsageToServer, getSessionWarmCandidates, loadWarmUsageProfile, recordSessionWarmNavigation, runControlledPriorityWarm, setWarmUsageIdentity, trackContextualWarmUsage, trackWarmUsage, warmUsageBaseModuleKey } from "./lib/warmControl";
import { UI_FONT_SCALE_OPTIONS, SHORTCUT_MODULE_CHOICES, DEFAULT_SHORTCUT_MODULES, DEFAULT_UI_PREFERENCES, LIGHT_THEME, DARK_THEME, normalizeUiPreferences, resolveThemeMode, applyThemeTokens, getContrastTextColor, ShortcutMonoIcon } from "./lib/theme";
import { readAdminPasswordOverride, writeAdminPasswordOverride, readAdminDefaultBlocked, persistAdminCredentialState, getPersistedAdminCredentialState, splitPersonNameParts, buildPersonDisplayName, normalizeLinkUsername, buildUniqueUsername, buildTemporaryAccessPassword, dedupeEmployeeLinkOptions, normalizeAuthAccount, normalizeAuthAccountsCollection, ensureDefaultAdminAccount, accountMergeKey, choosePreferredAccount, mergeAuthAccounts, getAccounts, saveAccounts, ensureAdminAccountAvailable } from "./lib/authUtils";
import { pickPreferredFarmRecord, buildCollectionIdentityKeys, buildCollectionIdentityKey, collectionItemsReferSameIdentity, choosePreferredCollectionItem, dedupeCollectionForBootstrap, mergeBootstrapVisibleWithServer, syncFarmCollaboratorAccountsToEmployees } from "./lib/collectionUtils";
import { SYSTEM_LISTS_LABELS, SYSTEM_LISTS_KEY, SYSTEM_LISTS_GLOBAL_KEY, normalizeSystemListsPayload } from "./lib/systemListsUtils";
import { loadLocalSystemLists, syncSystemListsFromServer, getSystemLists, saveSystemLists } from "./lib/systemListsStorage";
import { buildNextRecordNumber } from "./lib/recordNumberUtils";
import { migrateCollectionRecordNumbers, supportsRecordNumberMigration } from "./lib/recordNumberMigration";
import { matchesRecordNumberSearch } from "./lib/recordNumberSearch";
import { useSystemLists } from "./hooks/useSystemLists";
import { createIdempotencyKey, fetchWithTimeout, hasRealConnectivity, logSyncTelemetry } from "./sync/apiClient";
import { AUTO_LOGOUT_DEFAULT_MINUTES, pullAutoLogoutSettings, readCachedAutoLogoutSettings } from "./lib/autoLogoutSettings";
import { cacheLiveActivitySettings, initLiveActivitySettingsCache } from "./lib/liveActivitySettings";
import { getLiveCollectionCache, setLiveCollectionCache, readLatestCollectionState } from "./sync/cacheApi";
import { apiCall, pushCollection, pushOrderOps, pushOperationalOps, checkCollectionRevisions, pullCollection, pullCollectionDelta, pullFarmBundle, refreshCollectionFromServer, pushGlobal, pullGlobal, ensureServerInstall, mergeProtectedVisibleWithServer, checkDeviceBootstrapState, startDeviceBootstrapState, completeDeviceBootstrapState, failDeviceBootstrapState } from "./sync/collectionApi";
import { getFarmEmojiConfig, FarmEmoji, FarmInlineLabel, Btn, Field, Badge, CCBadge, Input, Select, Textarea, StatCard, Modal, InlineModal, Table, Toast, useToast, DenyModal, CotacaoModal, ReceiveModal, PwInput, inputStyle } from "./components/ui";
import { SVC_TYPES, SVC_PRIORITIES_INTERNAL, SVC_PRIORITIES_OCCURRENCE, SVC_PRIORITIES_EXTERNAL, getPriorities, getPriorityConfig, SVC_STATUS_FLOW, getEditableServiceStatuses, SVC_WARN_DAYS, SVC_ALERT_DAYS, daysSince, dateColor, ServiceDenyModal, PauseTaskModal, MaterialsModal, NFModal, ServicePhotoAddPhoneIcon, ServicePhotoStatusIcon, normalizePhotoEntries, ServicePhotosModal, ColActionButtons, ServiceDetailModal, roleColor } from "./components/ServicesUI";
import SystemListsManager from "./components/SystemListsManager";
import { createVetModule } from "./modules/createVetModule";
import { createLotDetailModal } from "./components/LotDetailModal";
import { createEquipamentosEPIsModule } from "./modules/createEquipamentosEPIsModule";
import { createMeuPerfilModule } from "./modules/createMeuPerfilModule";
import { buildClimateModule } from "./modules/ClimateModule";
import { createSuppliersModule } from "./modules/createSuppliersModule";
import { createProvidersModule } from "./modules/createProvidersModule";
import { createSedeModule } from "./modules/createSedeModule";
// ── Fase B extractions ─────────────────────────────────────────────────────
import { MODULE_CARD_LIBRARY, MODULE_CARD_SECTION_LIBRARY, getModuleCardSections, flattenModuleCardSections, getModuleCardLibrary, normalizeModuleCardsConfig } from "./lib/moduleCards";
import { getMaterialUsageQty, getRestockableMaterials, hasPendingMaterialRestock, hasCompletedMaterialRestock } from "./lib/materialHelpers";
import { MaterialRestockModal } from "./components/MaterialRestockModal";
import { MaintenanceModal } from "./components/MaintenanceModal";
import { PENDING_CONFLICTS_KEY, CONFLICT_RESOLUTIONS_KEY, SYNC_AUDIT_KEY, SYNC_HEALTH_KEY, USER_DELETION_LOG_KEY, buildDeletionTombstones, pruneDeletedAccounts, pruneDeletedEmployees, sanitizeGlobalCollectionForView, collectionLabel, describeConflictRecord, normalizeConflictFieldEntry, buildConflictSummary, normalizeConflictForUI, normalizeConflictListForUI, formatCollectionLabelSafe, parseQueueEntityKey, appendSyncAudit, loadSyncAuditFromStorage, saveSyncAuditToStorage, saveConflictResolutionsToStorage, loadConflictResolutionsFromStorage, saveSyncHealthSnapshot, loadPendingConflictsFromStorage, savePendingConflictsToStorage, createPendingConflict, buildConflictBusinessRows, formatConflictFieldLabel } from "./lib/conflictUtils";
import { ConflictModal } from "./components/ConflictModal";
import { SyncStatusBar } from "./components/SyncStatusBar";
import { AnimalOpcoes } from "./components/AnimalOpcoes";
import { MODULES } from "./lib/modulesConfig";
// ── Fase C extractions ─────────────────────────────────────────────────────
import { ADMIN_GLOBALS_CONFIG_DEFAULT, normalizeAdminGlobalsConfig } from "./lib/adminGlobals";
import { mergeAppendOnlyRaw, mergeAppendOnlyVisible } from "./lib/farmSyncUtils";
import { getTimeoutSignal, getUserDeletionLog, appendUserDeletionLog, getConflicts, resolveConflict, getConflictResolutions, undoConflictResolution } from "./lib/conflictApi";
import { parseNFe, mapUnit } from "./lib/nfeParser";
import { FarmCtx, useFarm, useGlobalCollection, useFarmCollection, normalizeFarmRecord, normalizeFarmsCollection, useFarms, normalizeFarmSpeciesList } from "./hooks/farmHooks";
import { setEditingFlag, useBgSyncToast, useSyncOnSave, useReliableSyncOnSave } from "./hooks/syncHooks";





const PROVIDER_SPECIALTIES = [
  "Eletricista","Pedreiro / Construção","Mecânico","Torneiro Mecânico",
  "Encanador / Hidráulico","Soldador","Pintor","Carpinteiro / Marceneiro",
  "Técnico em Refrigeração","Agrônomo","Veterinário Externo","Operador de Máquinas",
  "Técnico em Informática","Serralheiro","Topógrafo","Outros"
];

const pickRuntimeMapsKey = (source) => {
  const data = source && typeof source === "object" ? source : {};
  if (MAPS_PLATFORM === "ios") return String(data.googleMapsIosKey || data.iosKey || "").trim();
  if (MAPS_PLATFORM === "android") return String(data.googleMapsAndroidKey || data.androidKey || "").trim();
  return String(data.googleMapsSitesKey || data.googleMapsKey || data.key || "").trim();
};


const OCCURRENCE_CATEGORIES = [
  { id: "machines", icon: "🚜", label: "Máquinas e Equipamentos" },
  { id: "structure", icon: "🏗️", label: "Estrutura e Construção" },
  { id: "electric", icon: "⚡", label: "Elétrica e Hidráulica" },
  { id: "animal", icon: <CattleIcon size={18} />, label: "Animal / Veterinário" },
  { id: "other", icon: "📋", label: "Outro" },
];

const OCCURRENCE_STATUS = {
  "Aberta": { color: "#EF4444", icon: "🔴", order: 0 },
  "Prestador Acionado": { color: "#F97316", icon: "📞", order: 1 },
  "Orçamento Solicitado": { color: "#F59E0B", icon: "📄", order: 2 },
  "Orçamento Recebido": { color: "#3B82F6", icon: "💰", order: 3 },
  "Aguardando Aprovação": { color: "#8B5CF6", icon: "⏳", order: 4 },
  "Aguardando Reparo ou Peça": { color: "#EC4899", icon: "🔩", order: 5 },
  "Serviço Agendado": { color: "#0891B2", icon: "📅", order: 6 },
  "Em Execução": { color: "#F59E0B", icon: "⚡", order: 7 },
  "Pausada": { color: "#F97316", icon: "⏸️", order: 8 },
  "Concluída": { color: "#22C55E", icon: "✅", order: 9 },
  "Cancelada": { color: "#64748B", icon: "🚫", order: 9 },
};


const APP_VERSION = "3.11.01";

// v2.242.00: inicializar cache de Live Activity Settings com o último valor
// real da sessão anterior antes do primeiro render.
initLiveActivitySettingsCache();
const LOCATION_SYNC_DEBUG_KEY = "fz:admin:locationSyncStats:v2";

function readLocationSyncDebugSnapshot(){
  try{
    const raw = localStorage.getItem(LOCATION_SYNC_DEBUG_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : null;
  }catch(e){
    return null;
  }
}

const cursorKey = (farmId, col) => `fz:${farmId}:${col}:_cursor`;

function normalizeServiceType(service) {
  const rawType = String(service?.type || service?.serviceMode || service?.mode || "").trim().toLowerCase();
  const rawExec = String(service?.execType || service?.executionType || "").trim().toLowerCase();
  if ([rawType, rawExec].some(v => v.includes("external") || v.includes("externo") || v.includes("prestador") || v.includes("terceir"))) return "external";
  if ([rawType, rawExec].some(v => v.includes("internal") || v.includes("interno") || v.includes("colaborador"))) return "internal";
  return "internal";
}

// ═══════════════════════════════════════════════════════════════════════════════
// MaintenanceModal — shown to all users during maintenance window
// ═══════════════════════════════════════════════════════════════════════════════



// ═══════════════════════════════════════════════════════════════════════════════
// SYNC ENGINE
// ═══════════════════════════════════════════════════════════════════════════════
// Global flag: true when any form is open (prevents offline→online sync)

// Hook: shows toast when background sync updates data in current module
// Usage: useBgSyncToast("pastures", show)  — call at top of module component

// Stronger targeted sync for modules whose records were surviving only locally.
// It keeps the regular save-triggered sync and schedules two follow-up attempts
// to confirm the collection is pushed even if bootstrap/rehydration races happen.
const PURCHASE_CATS  = ["Insumos agrícolas","Equipamentos/Peças","Alimentação animal","Combustível"];
const CAT_ICONS      = { "Insumos agrícolas":"🌿","Equipamentos/Peças":"⚙️","Alimentação animal":<CattleIcon size={16} />,"Combustível":"⛽" };
// Order workflow statuses — automated transitions
const ORDER_STATUSES = ["Pendente","Em Análise","Aprovado","Parcial","Negado","Em Trânsito","Concluído","Cancelado"];
const ORDER_DENY_REASONS_DEFAULT = [
  "Preço acima do orçamento","Item não prioritário no momento","Produto já em estoque suficiente",
  "Fornecedor sem cadastro aprovado","Pedido duplicado","Aguardando aprovação de verba",
  "Item não compatível com as necessidades","Cotação insuficiente","Outro motivo"
];
const ORDER_STATUS_C = {
  "Pendente":           {bg:"rgba(245,158,11,.15)", text:"#FBB024",dot:"#FBB024", icon:"🕐"},
  "Em Análise":         {bg:"rgba(139,92,246,.15)", text:"#A78BFA",dot:"#A78BFA", icon:"🔍"},
  "Aprovado":           {bg:"rgba(34,197,94,.15)",  text:"#22C55E",dot:"#22C55E", icon:"✅"},
  "Parcial":            {bg:"rgba(59,130,246,.15)", text:"#60A5FA",dot:"#60A5FA", icon:"◐"},
  "Negado":             {bg:"rgba(239,68,68,.15)",  text:"#F87171",dot:"#F87171", icon:"❌"},
  "Em Trânsito": {bg:"rgba(59,130,246,.15)", text:"#60A5FA",dot:"#60A5FA", icon:"🚚"},
  "Concluído":          {bg:"rgba(16,185,129,.15)", text:"#34D399",dot:"#34D399", icon:"✅📦"},
  "Cancelado":          {bg:"rgba(100,116,139,.15)",text:"#94A3B8",dot:"#94A3B8", icon:"🚫"},
};
const PURCHASE_ITEM_STATUS_OPTIONS = ["Analisando","Aprovado","Negado","Parcial"];
const PURCHASE_ITEM_STATUS_META = {
  "Analisando": { bg:"rgba(245,158,11,.15)", text:"#F59E0B", border:"rgba(245,158,11,.35)" },
  "Aprovado":   { bg:"rgba(34,197,94,.15)", text:"#22C55E", border:"rgba(34,197,94,.35)" },
  "Negado":     { bg:"rgba(239,68,68,.15)", text:"#EF4444", border:"rgba(239,68,68,.35)" },
  "Parcial":    { bg:"rgba(59,130,246,.15)", text:"#3B82F6", border:"rgba(59,130,246,.35)" },
};
function getPurchaseItemStatus(item, orderStatus="") {
  const current = String(item?.itemStatus || item?.status || "").trim();
  if (PURCHASE_ITEM_STATUS_OPTIONS.includes(current)) return current;
  const order = String(orderStatus || "").trim();
  if (["Negado","Cancelado"].includes(order)) return "Negado";
  if (["Aprovado","Em Trânsito","Concluído"].includes(order)) return "Aprovado";
  return "Analisando";
}
const UNITS         = ["Unid.","Litros","Sacos","Kg","Caixas","Metros","Toneladas","Fardos"];
const ANIMAL_SEX    = ["Macho","Fêmea"];
const ANIMAL_STATUS = ["Ativo","Vendido","Óbito","Transferido"];
const FIN_TYPES     = ["Receita","Despesa"];
const FIN_CATS      = ["Venda de animais","Venda de produção","Compras","Salários","Manutenção","Combustível","Veterinário","Outros"];
const FUNC_ROLES    = ["Peão","Tratador","Veterinário","Motorista","Gerente","Administrativo","Outro"];
const FUNC_STATUS   = ["Ativo","Férias","Afastado","Desligado"];
const STOCK_CATS    = ["Ração","Suplementação","Medicamentos","Ferramentas","Sementes","Defensivos","Combustível","EPI","Outros"];
const STOCK_UNITS   = ["Kg","g","Litros","mL","Unid.","Sacos","Caixas","Fardos","Pacotes","Galões","Toneladas","Metros","m²","Rolo","Dose","Ampola","Frasco"];
const PASTURE_COLORS = [
  "#10B981","#3B82F6","#F59E0B","#EF4444","#8B5CF6",
  "#F97316","#06B6D4","#EC4899","#84CC16","#D97706",
];
const PASTURE_COLOR_NAMES = [
  "Verde","Azul","Âmbar","Vermelho","Roxo",
  "Laranja","Ciano","Rosa","Lima","Caramelo",
];

const MACH_STATUS   = ["Operacional","Manutenção","Inativo"];
// ═══════════════════════════════════════════════════════════════════════════════
// SEED — per-farm empty template
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// FARM CONTEXT
// ═══════════════════════════════════════════════════════════════════════════════
// ── useGlobalCollection — reactive collection shared across all farms ─────────
const AGE_RANGES = ["0-6","6-12","12-24","24-36","36+"];

const LotDetailModal = createLotDetailModal({
  AGE_RANGES,
});

const PurchasesModule = createPurchasesModule({
  useState,
  useEffect,
  useRef,
  useSysLists,
  useSync,
  useFarm,
  useFarms,
  useFarmCollection,
  useGlobalCollection,
  getAccounts,
  useToast,
  useReliableSyncOnSave,
  makeSeed,
  canCreateInModule,
  canEditInModule,
  canDeleteInModule,
  canViewCostCenter,
  canEditCostCenter,
  getAllowedCostCentersForModule,
  getDeniedCostCentersForModule,
  itemMatchesCostCenterAccess,
  itemEditableByCostCenter,
  itemCostCenters,
  compressImage,
  normalizePhotoEntries,
  createClientId,
  today,
  nextId,
  fmtMoney,
  fmtDate,
  T,
  COST_CENTERS,
  PURCHASE_CATS,
  UNITS,
  ORDER_STATUSES,
  ORDER_STATUS_C,
  ORDER_DENY_REASONS_DEFAULT,
  SYSTEM_LISTS_DEFAULTS,
  Btn,
  Modal,
  Field,
  Input,
  Select,
  Textarea,
  Toast,
  Badge,
  CotacaoModal,
  ReceiveModal,
  DenyModal,
  ServicePhotoAddPhoneIcon,
  ServicePhotoStatusIcon,
  makeCreateMeta,
  makeUpdateMeta,
  StatCard,
});




const LOT_PURPOSES = [
  "Engorda","Cria","Recria","Leite","Reprodutor","Descarte","Outro"
];

const FEED_TYPES = [
  "Pasto","Confinamento","Semi-confinamento","Suplementação a Pasto",
  "Silagem","Feno","Ração Total Misturada (RTM)","Outro"
];

const SUPPLEMENT_TYPES = [
  "Sal Mineral","Sal Proteinado","Sal Energético","Milho",
  "Sorgo","Farelo de Soja","Bagaço de Cana","Polpa Cítrica","Outro"
];

const WEIGHT_MODES = ["Peso Médio do Lote","Peso Individual por Animal"];



// ── AnimalOpcoes — manage breeds and purposes for livestock ──────────────────



// ── Lot Detail Modal ──────────────────────────────────────────────────────────
const EPI_DEFAULTS   = ["Luva","Bota","Máscara","Capacete","Óculos de proteção","Protetor auricular","Colete refletivo","Cinto de segurança"];
const EQUIP_DEFAULTS = ["Rádio","Caixa de ferramentas","Furadeira","Parafusadeira","Serra","Nível","Trena","Chave de fenda","Alicate","Martelo"];
const DEV_REASONS    = ["Normal","Estragou","Quebrou","Perdeu","Fim do contrato","Outro"];
const ITEM_CONDITIONS = [
  { value:"Conformidade",            label:"Em Conformidade",           color:"#10B981", icon:"✅" },
  { value:"Meia vida",               label:"Meia Vida",                 color:"#F59E0B", icon:"⚠️" },
  { value:"Mau uso",                 label:"Mau Uso",                   color:"#EF4444", icon:"⛔" },
  { value:"Quebrado",                label:"Quebrado",                  color:"#EF4444", icon:"🔴" },
  { value:"Usado conforme orientação",label:"Usado Conforme Orientação",color:"#3B82F6", icon:"👍" },
];

// Sub-component: checklist panel for a single employee
// HR extracted to src/modules/createHRModule.jsx

// ═══════════════════════════════════════════════════════════════════════════════
// VET
// ═══════════════════════════════════════════════════════════════════════════════
const STOCK_MOV_TYPES = [
  { id:"entrada",      label:"Entrada",                      icon:"➕", delta:+1, color:"#22C55E" },
  { id:"saida",        label:"Saída / Consumo",               icon:"➖", delta:-1, color:"#EF4444" },
  { id:"ajuste",       label:"Ajuste de Inventário",          icon:"⚖️", delta: 0, color:"#F59E0B" },
  { id:"transferencia",label:"Transferência entre C. Custo",  icon:"🔄", delta:-1, color:"#8B5CF6" },
  { id:"descarte",     label:"Descarte / Perda",              icon:"🗑️", delta:-1, color:"#94A3B8" },
  { id:"nfe",          label:"Entrada por NF-e",              icon:"📄", delta:+1, color:"#3B82F6" },
];
const ProvidersModule = createProvidersModule({
  useGlobalCollection,
  useSyncOnSave,
  canCreateInModule,
  canEditInModule,
  canDeleteInModule,
  PROVIDER_SPECIALTIES,
});

const StockModule = createStockModule({
  useState,
  useEffect,
  useRef,
  useCallback,
  useSysLists,
  useSync,
  useFarm,
  useFarmCollection,
  useGlobalCollection,
  getAccounts,
  useToast,
  useBgSyncToast,
  useSyncOnSave,
  makeSeed,
  canCreateInModule,
  canEditInModule,
  canDeleteInModule,
  canViewCostCenter,
  canEditCostCenter,
  getAllowedCostCentersForModule,
  getDeniedCostCentersForModule,
  itemMatchesCostCenterAccess,
  itemEditableByCostCenter,
  itemCostCenters,
  normalizePhotoEntries,
  compressImage,
  createClientId,
  nextId,
  today,
  fmtMoney,
  T,
  COST_CENTERS,
  STOCK_CATS,
  STOCK_UNITS,
  STOCK_MOV_TYPES,
  parseNFe,
  mapUnit,
  setEditingFlag,
  StatCard,
  Btn,
  Input,
  Field,
  Select,
  Textarea,
  Badge,
  Modal,
  Toast,
  ServicePhotoStatusIcon,
  ServicePhotoAddPhoneIcon,
  Table,
  CCBadge,
  fmtDate,
  inputStyle,
  isCompactViewport,
  makeCreateMeta,
  makeUpdateMeta,
});





// ═══════════════════════════════════════════════════════════════════════════════

const HRModule = createHRModule({
  useState,
  useEffect,
  makeSeed,
  useFarm,
  useFarms,
  useFarmCollection,
  useGlobalCollection,
  useSysLists,
  useToast,
  useBgSyncToast,
  useSync,
  useReliableSyncOnSave,
  canCreateInModule,
  canEditInModule,
  canDeleteInModule,
  canViewCostCenter,
  canEditCostCenter,
  getAllowedCostCentersForModule,
  getDeniedCostCentersForModule,
  itemMatchesCostCenterAccess,
  itemEditableByCostCenter,
  itemCostCenters,
  getAccounts,
  syncFarmCollaboratorAccountsToEmployees,
  DB,
  nextId,
  createClientId,
  makeCreateMeta,
  makeUpdateMeta,
  today,
  fmtMoney,
  fmtDate,
  inputStyle,
  setEditingFlag,
  saveAccounts,
  ensureDefaultAdminAccount,
  pushGlobal,
  appendUserDeletionLog,
  setLiveCollectionCache,
  isCompactViewport,
  T,
  COST_CENTERS,
  COLLAB_LEVELS,
  FUNC_ROLES,
  FUNC_STATUS,
  EPI_DEFAULTS,
  EQUIP_DEFAULTS,
  ITEM_CONDITIONS,
  DEV_REASONS,
  StatCard,
  Btn,
  Input,
  Field,
  Select,
  Textarea,
  Badge,
  Modal,
  Toast,
  CCBadge,
  PwInput,
  Table,
  FarmInlineLabel,
});




// LOCATION / POI
// ═══════════════════════════════════════════════════════════════════════════════
const GRASS_TYPES  = SYSTEM_LISTS_DEFAULTS.GRASS_TYPES;
const WEED_LEVELS  = SYSTEM_LISTS_DEFAULTS.WEED_LEVELS;
const POI_STATUSES = SYSTEM_LISTS_DEFAULTS.POI_STATUSES;
const { PasturesModule, POIModule } = createLocationModule({
  useState,
  useEffect,
  useRef,
  makeSeed,
  useSysLists,
  useFarm,
  useFarmCollection,
  useGlobalCollection,
  getAccounts,
  useToast,
  useBgSyncToast,
  useSync,
  useReliableSyncOnSave,
  canCreateInModule,
  canEditInModule,
  canDeleteInModule,
  canEditCostCenter,
  getAllowedCostCentersForModule,
  getDeniedCostCentersForModule,
  itemMatchesCostCenterAccess,
  itemEditableByCostCenter,
  T,
  COST_CENTERS,
  isCompactViewport,
  createClientId,
  fmtDate,
  GRASS_TYPES,
  WEED_LEVELS,
  POI_STATUSES,
  makeCreateMeta,
  makeUpdateMeta,
  Btn,
  Modal,
  Field,
  Input,
  Select,
  Textarea,
  StatCard,
  Toast,
  Badge,
  CCBadge,
  normalizeServiceType,
  PASTURE_COLORS,
  PASTURE_COLOR_NAMES,
  ServicePhotoStatusIcon,
  setEditingFlag,
});

// MACHINERY
// ═══════════════════════════════════════════════════════════════════════════════
const MachineryModule = createMachineryModule({
  useState,
  useEffect,
  makeSeed,
  useSysLists,
  useFarm,
  useFarmCollection,
  useGlobalCollection,
  getAccounts,
  useToast,
  useSync,
  useBgSyncToast,
  useReliableSyncOnSave,
  canCreateInModule,
  canEditInModule,
  canDeleteInModule,
  canViewCostCenter,
  canEditCostCenter,
  getAllowedCostCentersForModule,
  getDeniedCostCentersForModule,
  itemMatchesCostCenterAccess,
  itemEditableByCostCenter,
  itemCostCenters,
  T,
  MACH_TYPES,
  FUEL_TYPES,
  ENGINE_TYPES,
  COST_CENTERS,
  FUEL_KEYWORDS,
  createClientId,
  nextId,
  today,
  fmtDate,
  fmtDateTime,
  isCompactViewport,
  compressImage,
  makeCreateMeta,
  makeUpdateMeta,
  Btn,
  Modal,
  Field,
  Input,
  Select,
  Textarea,
  StatCard,
  Toast,
  Badge,
  CCBadge,
  Table,
  AnimalOpcoes,
  LotDetailModal,
});

const HarasModule = createHarasModule({
  useState,
  useEffect,
  useSysLists,
  useFarm,
  useFarmCollection,
  useGlobalCollection,
  getAccounts,
  useToast,
  useBgSyncToast,
  useReliableSyncOnSave,
  canViewCostCenter,
  canEditCostCenter,
  getDeniedCostCentersForModule,
  T,
  CC_COLORS,
  DEFAULT_SKIP_REASONS,
  nextId,
  createClientId,
  makeCreateMeta,
  makeUpdateMeta,
  DB,
  pushCollection,
  isCompactViewport,
  Btn,
  Modal,
  Field,
  Input,
  Select,
  Textarea,
  StatCard,
  Toast,
  Badge,
});
const AnimalsModule = createAnimalsModule({
  moduleId:"animals",
  moduleLabel:"Pecuária",
  speciesFilter:(species)=>species !== "Ave",
  useState,
  useEffect,
  makeSeed,
  useSysLists,
  useFarm,
  useFarmCollection,
  useGlobalCollection,
  getAccounts,
  useToast,
  useBgSyncToast,
  useReliableSyncOnSave,
  getAllowedCostCentersForModule,
  getDeniedCostCentersForModule,
  itemMatchesCostCenterAccess,
  canCreateInModule,
  canEditInModule,
  T,
  COST_CENTERS,
  CC_ICONS,
  CC_COLORS,
  ANIMAL_SPECIES,
  ANIMAL_SEX,
  ANIMAL_STATUS,
  AGE_RANGES,
  LOT_PURPOSES,
  FEED_TYPES,
  SUPPLEMENT_TYPES,
  WEIGHT_MODES,
  today,
  fmtDate,
  fmtMoney,
  isCompactViewport,
  createClientId,
  makeCreateMeta,
  makeUpdateMeta,
  setEditingFlag,
  Btn,
  Modal,
  Field,
  Input,
  Select,
  Textarea,
  StatCard,
  Toast,
  Badge,
  CCBadge,
  Table,
  AnimalOpcoes,
  LotDetailModal,
});

const AvicultureModule = createAnimalsModule({
  moduleId:"aviculture",
  moduleLabel:"Avicultura",
  speciesFilter:(species)=>species === "Ave",
  defaultSpeciesOverride:"Ave",
  useState,
  useEffect,
  makeSeed,
  useSysLists,
  useFarm,
  useFarmCollection,
  useGlobalCollection,
  getAccounts,
  useToast,
  useBgSyncToast,
  useReliableSyncOnSave,
  getAllowedCostCentersForModule,
  getDeniedCostCentersForModule,
  itemMatchesCostCenterAccess,
  canCreateInModule,
  canEditInModule,
  T,
  COST_CENTERS,
  CC_ICONS,
  CC_COLORS,
  ANIMAL_SPECIES,
  ANIMAL_SEX,
  ANIMAL_STATUS,
  AGE_RANGES,
  LOT_PURPOSES,
  FEED_TYPES,
  SUPPLEMENT_TYPES,
  WEIGHT_MODES,
  today,
  fmtDate,
  fmtMoney,
  isCompactViewport,
  createClientId,
  makeCreateMeta,
  makeUpdateMeta,
  setEditingFlag,
  Btn,
  Modal,
  Field,
  Input,
  Select,
  Textarea,
  StatCard,
  Toast,
  Badge,
  CCBadge,
  Table,
  AnimalOpcoes,
  LotDetailModal,
});

const ClimateModule = buildClimateModule({
  useState,
  useEffect,
  useMemo,
  useCallback,
  useFarm,
  useFarmCollection,
  useGlobalCollection,
  useToast,
  ModuleHeaderMenu,
  Btn,
  Badge,
  Toast,
  T,
  fmtDate,
});

const FinancialModule = createFinancialModule({
  useState,
  useEffect,
  makeSeed,
  useSysLists,
  useFarm,
  useFarmCollection,
  useGlobalCollection,
  getAccounts,
  useToast,
  useBgSyncToast,
  useReliableSyncOnSave,
  canCreateInModule,
  canEditInModule,
  canDeleteInModule,
  canViewCostCenter,
  getAllowedCostCentersForModule,
  getDeniedCostCentersForModule,
  T,
  FIN_CATS,
  FIN_TYPES,
  COST_CENTERS,
  today,
  fmtDate,
  fmtMoney,
  isCompactViewport,
  createClientId,
  makeCreateMeta,
  makeUpdateMeta,
  Btn,
  Modal,
  Field,
  Input,
  Select,
  Textarea,
  StatCard,
  Toast,
  Badge,
  CCBadge,
  Table,
});

const InspectionsModule = createInspectionsModule({
  useState,
  useEffect,
  useFarm,
  useFarmCollection,
  useGlobalCollection,
  useSysLists,
  useToast,
  useBgSyncToast,
  useReliableSyncOnSave,
  getAccounts,
  createClientId,
  makeCreateMeta,
  makeUpdateMeta,
  canCreateInModule,
  canEditInModule,
  canViewCostCenter,
  canEditCostCenter,
  getAllowedCostCentersForModule,
  getDeniedCostCentersForModule,
  itemMatchesCostCenterAccess,
  T,
  COST_CENTERS,
  COLLAB_LEVELS,
  today,
  fmtDate,
  Btn,
  Modal,
  Field,
  Input,
  Select,
  Textarea,
  StatCard,
  Toast,
  Badge,
  CCBadge,
});

// SERVICES
// ═══════════════════════════════════════════════════════════════════════════════
const ServicesModule = createServicesModule({
  useState,
  useEffect,
  useSysLists,
  useSync,
  makeSeed,
  useFarmCollection,
  useGlobalCollection,
  useFarm,
  useToast,
  useReliableSyncOnSave,
  canCreateInModule,
  canEditInModule,
  canDeleteInModule,
  canViewCostCenter,
  canEditCostCenter,
  getAllowedCostCentersForModule,
  itemMatchesCostCenterAccess,
  SYSTEM_LISTS_DEFAULTS,
  enrichStockItemsWithQty,
  daysSince,
  dateColor,
  normalizeServiceType,
  getPriorityConfig,
  getPriorities,
  getEditableServiceStatuses,
  getAccounts,
  createClientId,
  today,
  fmtMoney,
  fmtDate,
  T,
  isCompactViewport,
  Btn,
  Field,
  Input,
  Select,
  Textarea,
  StatCard,
  Toast,
  Badge,
  ProvidersModule,
  ServiceDenyModal,
  ServicePhotoAddPhoneIcon,
  ServicePhotoStatusIcon,
  OCCURRENCE_CATEGORIES,
  SVC_STATUS_FLOW,
  SVC_TYPES,
  getMaterialUsageQty,
  getRestockableMaterials,
  hasCompletedMaterialRestock,
  hasPendingMaterialRestock,
  makeCreateMeta,
  makeUpdateMeta,
  compressImage,
});


function PurchasesHubModule({ session, isGestor, openOrderId, onClearOpenOrder, initialTab="orders", showSuppliersTab=true, purchaseEditWindowConfig, onBridgeBack }){
  const suppliersVisible = session?.role === "admin" && showSuppliersTab !== false;
  const safeInitialTab = suppliersVisible ? (initialTab||"orders") : "orders";
  const [tab,setTab]=useState(safeInitialTab);
  useEffect(()=>{ setTab(suppliersVisible ? (initialTab||"orders") : "orders"); },[initialTab, suppliersVisible]);
  return (
    <div>
      <ModuleHeaderMenu
        T={T}
        title="Menu de Compras"
        items={[
          { id:"orders", label:"Compras", icon:"🛒", active:tab==="orders", onClick:()=>setTab("orders") },
          ...(suppliersVisible ? [{ id:"suppliers", label:"Fornecedores", icon:"🏭", active:tab==="suppliers", onClick:()=>setTab("suppliers") }] : []),
        ]}
      />
      {tab==="suppliers" && suppliersVisible ? (
        <SuppliersModule session={session} isGestor={isGestor}/>
      ) : (
        <PurchasesModule session={session} isGestor={isGestor} openOrderId={openOrderId} onClearOpenOrder={onClearOpenOrder} purchaseEditWindowConfig={purchaseEditWindowConfig} onBridgeBack={onBridgeBack}/>
      )}
    </div>
  );
}

function LocationHubModule({ session, isGestor, initialTab="location", poiPreset, onConsumePoiPreset }){
  const normalizedInitialTab = initialTab === "poi" ? "poi" : "location";
  const [tab,setTab]=useState(normalizedInitialTab||"location");
  const [locationView,setLocationView]=useState(initialTab === "places" ? "list" : "overview");
  const [localPoiPreset,setLocalPoiPreset]=useState(poiPreset||null);
  useEffect(()=>{
    setTab(normalizedInitialTab||"location");
    if(initialTab === "places") setLocationView("list");
    else if(initialTab === "location") setLocationView("overview");
  },[initialTab, normalizedInitialTab]);
  useEffect(()=>{ setLocalPoiPreset(poiPreset||null); },[poiPreset]);
  const isFirefoxMac = useMemo(()=>{
    try {
      const ua = String(window?.navigator?.userAgent || "");
      return /Firefox\//i.test(ua) && /(Macintosh|Mac OS X)/i.test(ua);
    } catch(_) {
      return false;
    }
  },[]);  if (isFirefoxMac) {
    return (
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:16,boxShadow:T.shadow}}>
        <div style={{display:"grid",gap:12}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:24,fontWeight:700,color:T.text}}>🗺️ Localização</div>
          <div style={{color:T.muted,lineHeight:1.6}}>
            O módulo Localização não está disponível no Firefox para macOS no momento.
            Para usar esse módulo, abra o app no Chrome, Safari ou Firefox no Windows.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div>
      <ModuleHeaderMenu
        T={T}
        title="Menu de Localização"
        items={[
          {
            id:"location-group",
            label:"Localização",
            icon:"🗺️",
            active:tab==="location",
            children:[
              { id:"location-overview", label:"Mapa Geral", icon:"🗺️", active:tab==="location" && locationView==="overview", onClick:()=>{ setLocationView("overview"); setTab("location"); } },
              { id:"location-places", label:"Lugares", icon:"🌿", active:tab==="location" && locationView==="list", onClick:()=>{ setLocationView("list"); setTab("location"); } },
            ],
          },
          { id:"poi", label:"Pontos de Interesse", icon:"📍", active:tab==="poi", onClick:()=>setTab("poi") },
        ]}
      />
      {tab==="location" ? (
        <PasturesModule session={session} isGestor={isGestor} hideHeaderMenu initialTab={locationView} onOpenPOI={(preset)=>{ setLocalPoiPreset(preset||null); setTab("poi"); }}/>
      ) : (
        <POIModule session={session} preset={localPoiPreset} onConsumePreset={()=>{ setLocalPoiPreset(null); onConsumePoiPreset&&onConsumePoiPreset(); }}/>
      )}
    </div>
  );
}

// MODULES importado de ./lib/modulesConfig



const VISIBLE_MODULES = MODULES.filter(m=>!m.hidden);

function getShortcutChoiceMeta(moduleId){
  const labels = {
    services:"Serviços", purchases:"Compras", occurrences:"Ocorrências", inspections:"Vistorias",
    animals:"Pecuária", aviculture:"Avicultura", machinery:"Maquinário", vet:"Veterinário", haras:"Haras", services_external:"Serviços Terceirizados",
  };
  return { id: moduleId, label: labels[moduleId] || moduleId, iconType: moduleId };
}

const PERMISSION_ACTIONS = ["view","create","edit","delete"];

function emptyPermissionSet(){
  return {view:false,create:false,edit:false,delete:false};
}

function buildAllModulePermissions(fill=false){
  return Object.fromEntries(MODULES.map(m=>[m.id,{view:fill,create:fill,edit:fill,delete:fill}]));
}

function normalizeModulePermissions(modulePermissions, allowedModules, role){
  if(role==="admin"||role==="gestor") return buildAllModulePermissions(true);
  const normalized = buildAllModulePermissions(false);
  const source = modulePermissions && typeof modulePermissions === "object" ? modulePermissions : null;
  if(source){
    for(const m of MODULES){
      const raw = source[m.id] || {};
      const view = !!raw.view || !!raw.create || !!raw.edit || !!raw.delete;
      normalized[m.id] = {
        view,
        create: !!raw.create,
        edit: !!raw.edit,
        delete: !!raw.delete,
      };
    }
    return normalized;
  }
  const legacy = Array.isArray(allowedModules) ? allowedModules : [];
  const allowAll = legacy.length===0;
  const denyAll = legacy.includes("__none__");
  for(const m of MODULES){
    const view = !denyAll && (allowAll || legacy.includes(m.id));
    normalized[m.id] = {view,create:false,edit:false,delete:false};
  }
  return normalized;
}

function deriveAllowedModules(modulePermissions, role){
  if(role!=="solicitante") return [];
  const perms = normalizeModulePermissions(modulePermissions, [], role);
  const viewed = MODULES.filter(m=>perms[m.id]?.view).map(m=>m.id);
  if(viewed.length===0) return ["__none__"];
  if(viewed.length===MODULES.length) return [];
  return viewed;
}

function emptyCostCenterPermissionSet(){ return {view:false, edit:false}; }
function buildAllCostCenterPermissions(fill=false){ return Object.fromEntries(MODULES.map(m=>[m.id, Object.fromEntries(COST_CENTERS.map(cc=>[cc,{view:!!fill, edit:!!fill}]))])); }
function normalizeCostCenterPermissions(costCenterPermissions, role){
  if(role==="admin"||role==="gestor") return buildAllCostCenterPermissions(true);
  const normalized = buildAllCostCenterPermissions(false);
  const source = costCenterPermissions && typeof costCenterPermissions === "object" ? costCenterPermissions : null;
  if(!source) return normalized;
  for(const m of MODULES){ const moduleSource = source?.[m.id]; if(!moduleSource || typeof moduleSource !== "object") continue; for(const cc of COST_CENTERS){ const raw = moduleSource?.[cc]; if(raw && typeof raw === "object") normalized[m.id][cc] = {view:!!raw.view, edit:!!raw.edit}; else if(raw===true) normalized[m.id][cc] = {view:true, edit:true}; }}
  return normalized;
}
function getCostCenterPermission(session, moduleId, costCenter){
  if(session?.role==="admin"||session?.role==="gestor") return {view:true, edit:true};
  const cc = String(costCenter||"").trim(); if(!cc) return {view:true, edit:true};
  const normalized = normalizeCostCenterPermissions(session?.costCenterPermissions, session?.role);
  const alias = moduleId==="services_external" ? "services" : (moduleId==="my_items" ? "hr" : moduleId);
  const direct = ((normalized[moduleId]||{})[cc]) || ((normalized[alias]||{})[cc]) || emptyCostCenterPermissionSet();
  const modulePerm = getModulePermission(session, moduleId);
  return { view:!!(modulePerm.view && direct.view), edit:!!(modulePerm.edit && direct.edit) };
}
function canViewCostCenter(session, moduleId, costCenter){ return !!getCostCenterPermission(session,moduleId,costCenter).view; }
function canEditCostCenter(session, moduleId, costCenter){ return !!getCostCenterPermission(session,moduleId,costCenter).edit; }
function getAllowedCostCentersForModule(session, moduleId){ if(session?.role==="admin"||session?.role==="gestor") return [...COST_CENTERS]; return COST_CENTERS.filter(cc=>canViewCostCenter(session,moduleId,cc)); }
function getDeniedCostCentersForModule(session, moduleId){ if(session?.role==="admin"||session?.role==="gestor") return []; return COST_CENTERS.filter(cc=>!canViewCostCenter(session,moduleId,cc)); }
function itemCostCenters(item, fallback=""){ const arr = Array.isArray(item?.costCenters) ? item.costCenters.filter(Boolean).map(v=>String(v).trim()) : []; const single = String(item?.costCenter || fallback || "").trim(); return [...new Set([...(arr||[]), ...(single?[single]:[])])]; }
function itemMatchesCostCenterAccess(session, moduleId, item, fallback=""){ const centers=itemCostCenters(item,fallback); if(!centers.length) return true; return centers.some(cc=>canViewCostCenter(session,moduleId,cc)); }
function itemEditableByCostCenter(session, moduleId, item, fallback=""){ const centers=itemCostCenters(item,fallback); if(!centers.length) return true; return centers.every(cc=>canEditCostCenter(session,moduleId,cc)); }

function getModulePermission(session, moduleId){
  if(session?.role==="admin"||session?.role==="gestor") return {view:true,create:true,edit:true,delete:true};
  const normalized = normalizeModulePermissions(session?.modulePermissions, session?.allowedModules, session?.role);
  if(normalized[moduleId]){
    const direct = normalized[moduleId] || emptyPermissionSet();
    if(!["services_external","my_items"].includes(moduleId)) return direct;
    const directHasAny = direct.view || direct.create || direct.edit || direct.delete;
    if(directHasAny) return direct;
  }
  if(moduleId==="services_external") return normalized["services"] || emptyPermissionSet();
  if(moduleId==="my_items") return normalized["hr"] || emptyPermissionSet();
  return normalized[moduleId] || emptyPermissionSet();
}

function canViewModule(session, moduleId){
  return !!getModulePermission(session, moduleId).view;
}
function getEffectiveBootstrapState(bootstrapState){
  if (bootstrapState) return bootstrapState;
  try { return window.__fzBootstrapState || null; } catch (_) { return null; }
}
// v3.9.12: bootstrapState como terceiro argumento opcional.
// Bloqueia ações operacionais enquanto bootstrap não estiver ready.
// not_started/null = farm ainda não iniciou bootstrap = sem bloqueio.
function canCreateInModule(session, moduleId, bootstrapState){
  const bs = getEffectiveBootstrapState(bootstrapState);
  if (bs && bs.status !== "ready" && bs.status !== "not_started") return false;
  return !!getModulePermission(session, moduleId).create;
}
function canEditInModule(session, moduleId, bootstrapState){
  const bs = getEffectiveBootstrapState(bootstrapState);
  if (bs && bs.status !== "ready" && bs.status !== "not_started") return false;
  return !!getModulePermission(session, moduleId).edit;
}
function canDeleteInModule(session, moduleId, bootstrapState){
  const bs = getEffectiveBootstrapState(bootstrapState);
  if (bs && bs.status !== "ready" && bs.status !== "not_started") return false;
  return !!getModulePermission(session, moduleId).delete;
}

// ═══════════════════════════════════════════════════════════════════════════════
// FARM DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════

// Dashboard cards that admin can control per user
const STOCK_CARD_LIBRARY = [
  { id:"ration_bovino", label:"Duração de Ração · Bovinos", type:"duration", category:"Ração", species:"Bovino", subLabel:"Bovinos" },
  { id:"supplement_bovino", label:"Duração de Suplemento · Bovinos", type:"duration", category:"Suplementação", species:"Bovino", subLabel:"Bovinos" },
  { id:"ration_equino", label:"Duração de Ração · Equinos", type:"duration", category:"Ração", species:"Equino", subLabel:"Equinos" },
  { id:"supplement_equino", label:"Duração de Suplemento · Equinos", type:"duration", category:"Suplementação", species:"Equino", subLabel:"Equinos" },
  { id:"ration_ave_postura", label:"Duração de Ração · Aves Postura", type:"duration", category:"Ração", species:"Ave", subcategory:"Aves Postura", subLabel:"Aves Postura" },
  { id:"ration_ave_crescimento", label:"Duração de Ração · Aves Crescimento", type:"duration", category:"Ração", species:"Ave", subcategory:"Aves Crescimento", subLabel:"Aves Crescimento" },
  { id:"feno_equino", label:"Duração de Feno · Equinos", type:"duration", category:"Feno", species:"Equino", subLabel:"Equinos" },
  { id:"qty_palha_arroz", label:"Qtd. Palha de Arroz", type:"quantity", nameIncludes:"palha de arroz" },
];
const DEFAULT_STOCK_CARD_IDS = ["ration_bovino","supplement_bovino","ration_equino","ration_ave_postura"];
function getStockCardsConfig(){
  try {
    const saved = JSON.parse(localStorage.getItem("fz:stockCardsConfig") || "null");
    if (Array.isArray(saved) && saved.length) return saved.filter(Boolean).slice(0,4);
  } catch(e) {}
  return DEFAULT_STOCK_CARD_IDS;
}
async function saveStockCardsConfig(ids){
  const next = (Array.isArray(ids) ? ids : []).filter(Boolean).slice(0,4);
  localStorage.setItem("fz:stockCardsConfig", JSON.stringify(next.length ? next : DEFAULT_STOCK_CARD_IDS));
  window.dispatchEvent(new CustomEvent("fz:stockcards:updated"));
}

const DASHBOARD_CARDS = [
  {id:"services_occurrences", label:"Serviços & Ocorrências", icon:"🔨", color:"#3B82F6"},
  {id:"purchases_overview",   label:"Compras",                icon:"🛒", color:"#F59E0B"},
  {id:"stock_alerts",         label:"Estoque",                icon:"📦", color:"#10B981"},
];

function useDisplayedAppVersion() {
  const [displayVersion] = useState(() => window.APP_VERSION || CLIENT_VERSION || APP_VERSION);
  return displayVersion;
}



// ═══════════════════════════════════════════════════════════════════════════════

const OccurrencesModule = createOccurrencesModule({
  useState,
  useEffect,
  useSysLists,
  useSync,
  useFarmCollection,
  useGlobalCollection,
  useFarm,
  useToast,
  useReliableSyncOnSave,
  canCreateInModule,
  canEditInModule,
  canDeleteInModule,
  canViewCostCenter,
  canEditCostCenter,
  getAllowedCostCentersForModule,
  itemMatchesCostCenterAccess,
  itemCostCenters,
  SYSTEM_LISTS_DEFAULTS,
  enrichStockItemsWithQty,
  normalizePhotoEntries,
  getMaterialUsageQty,
  getRestockableMaterials,
  hasPendingMaterialRestock,
  hasCompletedMaterialRestock,
  getPriorityConfig,
  getPriorities,
  getAccounts,
  createClientId,
  today,
  fmtMoney,
  fmtDate,
  fmtDateTime,
  daysSince,
  T,
  isCompactViewport,
  Btn,
  Modal,
  Field,
  Input,
  Select,
  Textarea,
  StatCard,
  Toast,
  Badge,
  MaterialsModal,
  MaterialRestockModal,
  PauseTaskModal,
  ServicePhotosModal,
  ServicePhotoAddPhoneIcon,
  ServicePhotoStatusIcon,
  OCCURRENCE_CATEGORIES,
  OCCURRENCE_STATUS,
  makeCreateMeta,
  makeUpdateMeta,
  compressImage,
});

// ═══════════════════════════════════════════════════════════════════════════════
// FASE A — Módulos extraídos de App.jsx
// ═══════════════════════════════════════════════════════════════════════════════



const VetModule = createVetModule({
  useFarm,
  useFarmCollection,
  useGlobalCollection,
  normalizeAdminGlobalsConfig,
  canCreateInModule,
  canEditInModule,
  canDeleteInModule,
  useSyncOnSave,
  useBgSyncToast,
  AGE_RANGES,
});



const EquipamentosEPIsModule = createEquipamentosEPIsModule({
  useFarmCollection,
  ITEM_CONDITIONS,
});

const MeuPerfilModule = createMeuPerfilModule({
  useFarmCollection,
  useSyncOnSave,
  canViewModule,
  getShortcutChoiceMeta,
});

const SuppliersModule = createSuppliersModule({
  useGlobalCollection,
  useSyncOnSave,
  PURCHASE_CATS,
  PROVIDER_SPECIALTIES,
});

const SedeModule = createSedeModule({
  useFarmCollection,
  canViewCostCenter,
  normalizeServiceType,
  MODULES,
  deriveAllowedModules,
  normalizeModulePermissions,
});



// ── Unified Service Model ─────────────────────────────────────────────────────
// Collections that belong to a specific farm
function getSyncGlobalDeps() {
  return {
    DB,
    GLOBAL_COLLECTIONS,
    GLOBAL_SYNC_FRESHNESS_MS,
    GLOBAL_KEY,
    pullGlobal,
    pushGlobal,
    dedupeCollectionForBootstrap,
    collectionItemsReferSameIdentity,
    choosePreferredCollectionItem,
    windowObj: typeof window !== "undefined" ? window : null,
  };
}

function getWarmControlDeps() {
  return {
    requestWarmScope,
    syncGlobalCollections,
    getSyncGlobalDeps,
    DB,
    opsKey,
    pendingKey,
    pullCollection,
    mergeBootstrapVisibleWithServer,
    setLiveCollectionCache,
    shadowKey,
    verKey,
    cursorKey,
    windowObj: typeof window !== "undefined" ? window : null,
  };
}


function isFarmArchived(farm) {
  return !!(farm?.archivedAt || farm?.isArchived);
}

function getNativeGenerationOperationalCollections() {
  return Array.from(new Set([
    ...Array.from(FARM_GENERATION_OPERATIONAL_COLLECTIONS || []),
  ])).filter(Boolean);
}

async function clearLocalStateForFarmGeneration({ farmId, farm, collections, userName = "system" } = {}) {
  const fid = String(farmId || farm?.id || "").trim();
  if (!fid) return { cleared: 0 };
  const cols = Array.isArray(collections) && collections.length ? collections : getNativeGenerationOperationalCollections();
  let cleared = 0;
  for (const col of cols) {
    const collection = String(col || "").trim();
    if (!collection) continue;
    const localKey = `fz:${fid}:${collection}`;
    const keys = [localKey, shadowKey(fid, collection), opsKey(fid, collection), pendingKey(fid, collection)];
    for (const key of keys) {
      try { localStorage.removeItem(key); } catch (_) {}
      try { await DB.set(key, []); } catch (_) {}
    }
    try { localStorage.removeItem(verKey(fid, collection)); } catch (_) {}
    try { localStorage.removeItem(cursorKey(fid, collection)); } catch (_) {}
    try { await DB.set(verKey(fid, collection), null); } catch (_) {}
    try { await DB.set(cursorKey(fid, collection), null); } catch (_) {}
    try { setLiveCollectionCache(localKey, []); } catch (_) {}
    try { setLiveCollectionCache(shadowKey(fid, collection), []); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: localKey, source: "native-generation-clear" } })); } catch (_) {}
    cleared += 1;
  }
  try {
    localStorage.setItem("fz:farmGeneration:lastNativeRefresh", JSON.stringify({
      farmId: fid,
      activeGeneration: getActiveFarmGeneration(farm),
      collections: cleared,
      by: userName,
      at: new Date().toISOString(),
    }));
  } catch (_) {}
  return { cleared };
}

async function refreshNativeFarmGenerationFromServer({ farm, userName = "system" } = {}) {
  const fid = String(farm?.id || "").trim();
  if (!fid || !navigator.onLine) return { refreshed: false, reason: !fid ? "no-farm" : "offline" };
  const collections = getNativeGenerationOperationalCollections();
  // v3.9.12: pullFarmBundle exige source:"protected_bootstrap" — não usar aqui.
  // Substituir por pullCollection individual para cada collection de geração.
  let refreshed = 0;
  const data = {};
  for (const col of collections) {
    try {
      const res = await pullCollection(fid, col, userName, { forceFull: false });
      if (res?.data) data[col] = { data: res.data, version: res.version, cursor: res.cursor };
    } catch (_) {}
  }
  if (Object.keys(data).length === 0) return { refreshed: false, reason: "no-data" };
  for (const col of collections) {
    const localKey = `fz:${fid}:${col}`;
    const entry = data?.[col];
    const raw = Array.isArray(entry?.data) ? entry.data : (Array.isArray(entry) ? entry : []);
    const activeGeneration = getActiveFarmGeneration(farm);
    const filteredRaw = raw.filter((item) => {
      try {
        const itemGeneration = Number(item?.dataGeneration ?? item?.farmDataGeneration ?? item?.generation ?? 1) || 1;
        return itemGeneration === activeGeneration;
      } catch (_) {
        return activeGeneration === 1;
      }
    });
    try { await DB.set(localKey, filteredRaw); } catch (_) {}
    try { await DB.set(shadowKey(fid, col), filteredRaw); } catch (_) {}
    try { setLiveCollectionCache(localKey, filteredRaw); } catch (_) {}
    try { setLiveCollectionCache(shadowKey(fid, col), filteredRaw); } catch (_) {}
    if (entry && typeof entry === "object" && !Array.isArray(entry)) {
      try { await DB.set(verKey(fid, col), entry?.version || 0); } catch (_) {}
      try { await DB.set(cursorKey(fid, col), entry?.cursor || null); } catch (_) {}
    }
    try { window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: localKey, source: "native-generation-pull" } })); } catch (_) {}
    refreshed += 1;
  }
  return { refreshed: true, collections: refreshed };
}

// Collections that are GLOBAL (shared across all farms)



// ── App Version Check System ─────────────────────────────────────────────────
const CLIENT_VERSION = window.APP_VERSION || "0.0.0";



// Global online status
let _isOnline = navigator.onLine;
const _onlineListeners = new Set();
function subscribeOnline(fn) { _onlineListeners.add(fn); return () => _onlineListeners.delete(fn); }
window.addEventListener("online",  () => { _isOnline=true;  _onlineListeners.forEach(f=>f(true));  });
window.addEventListener("offline", () => { _isOnline=false; _onlineListeners.forEach(f=>f(false)); });

function AppWithSync(){
  const [session, setSession] = useStoredSessionState();
  useEffect(()=>{
    setWarmUsageIdentity(session);
    window.__fzFlushWarmUsage = () => flushWarmUsageToServer(window.__fzWarmUsageIdentity || session || null);
    return () => {
      try { delete window.__fzFlushWarmUsage; } catch (_) {}
    };
  }, [session]);

  useEffect(()=>{
    if(!session?.id && !session?.userId && !session?.username) return;
    loadWarmUsageProfile(session).catch(() => {});
    flushWarmUsageToServer(session).catch(() => {});
  }, [session?.id, session?.userId, session?.username]);

  useEffect(()=>{
    if(!session?.id && !session?.userId && !session?.username) return;
    const flushNow = ()=>{ flushWarmUsageToServer(session).catch(() => {}); };
    window.addEventListener("online", flushNow);
    window.addEventListener("focus", flushNow);
    document.addEventListener("visibilitychange", flushNow);
    return ()=>{
      window.removeEventListener("online", flushNow);
      window.removeEventListener("focus", flushNow);
      document.removeEventListener("visibilitychange", flushNow);
    };
  }, [session?.id, session?.userId, session?.username]);
  const { themeRevision, currentUiPreferences, currentThemeMode, effectiveFontScale, currentZoom } = useAppPreferences(session);
  const vcheck = useVersionCheck(!!session, {
    isOnline: _isOnline,
    CLIENT_VERSION,
    fetchServerVersion,
    requestWarmScope,
    allowTechnicalReloadWindow,
    performSoftReloadToLatest,
    waitForNextController,
    waitForServiceWorkerActivationAck,
    buildUpdateUrl,
    windowObj: typeof window !== "undefined" ? window : null,
  });
  const displayVersion = useDisplayedAppVersion();
  const [activeFarm, setActiveFarm] = useStoredActiveFarmState();

  useEffect(() => {
    if (!isNativeShell()) return undefined;
    let disposed = false;
    restoreSessionFromIdbIfNeeded().then((result) => {
      if (disposed || !result?.restored) return;
      const restoredSession = readStoredSession();
      const restoredFarm = readStoredActiveFarm();
      if (restoredSession) {
        setSession((current) => current || restoredSession);
      }
      if (restoredFarm) {
        setActiveFarm((current) => current || restoredFarm);
      }
    }).catch(() => {});
    return () => { disposed = true; };
  }, []);

  const [farms] = useFarms();
  const [adminGlobalsConfigRows] = useGlobalCollection("adminGlobalsConfig", [normalizeAdminGlobalsConfig(null)]);
  const adminGlobalsConfig = useMemo(()=>normalizeAdminGlobalsConfig((adminGlobalsConfigRows||[])[0]), [adminGlobalsConfigRows]);
  const [nativeShellToast, showNativeShellToast] = useToast();

  useEffect(()=>{
    cacheLiveActivitySettings(adminGlobalsConfig?.liveActivitySettings);
  }, [adminGlobalsConfig?.liveActivitySettings]);
  // v2.242.00: backgroundFastSyncRequested is handled by SyncProvider.requestPrioritySync.
  // This avoids forceFull pulls and keeps the silent-push path free of APNs/notify loops.


  useEffect(()=>{
    if (!session) return undefined;
    let disposed = false;
    const applyLiveDeepLink = (target) => {
      if (!target || disposed) return;
      try {
        const detail = {
          ...target,
          module: String(target.module || "purchases"),
          recordId: String(target.recordId || target.orderId || ""),
          orderId: String(target.orderId || target.recordId || ""),
          farmId: String(target.farmId || ""),
          receivedAt: target.receivedAt || new Date().toISOString(),
        };
        if (!detail.recordId && !detail.orderId) return;
        // v2.242.00: diagnóstico server-side do toque na Live em condição real, sem depender do Xcode.
        reportLiveTouchDiagnosticToServer({
          ...detail,
          stage: "app_apply_deeplink",
          collection: detail.module === "purchases" ? "orders" : detail.module,
          routeOpened: "pending_dashboard_dispatch",
          deviceId: getDeviceId(),
          userName: session?.name || session?.username || session?.userId || "",
          username: session?.username || session?.name || "",
          userId: session?.userId || session?.id || "",
          payload: target,
        }).catch(()=>{});
        sessionStorage.setItem("fz:pendingLiveActivityDeepLink", JSON.stringify(detail));
        // O FarmDashboard faz o sync prioritário do registro ao receber o open-record.
        // Evita disparar o mesmo priority-sync duas vezes no boot nativo.
        try { window.dispatchEvent(new CustomEvent("fz:live-activity-open-record", { detail })); } catch (_) {}
        try {
          const url = new URL(window.location.href);
          url.searchParams.set("module", detail.module || "purchases");
          url.searchParams.set("orderId", detail.orderId || detail.recordId);
          if (detail.farmId) url.searchParams.set("farmId", detail.farmId);
          window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
        } catch (_) {}
      } catch (_) {}
    };
    const sub = subscribeLiveActivityDeepLink(applyLiveDeepLink);
    getPendingLiveActivityDeepLink().then((target)=>{
      if (!target || disposed) return;
      applyLiveDeepLink(target);
      clearPendingLiveActivityDeepLink().catch(()=>{});
    }).catch(()=>{});
    return ()=>{
      disposed = true;
      try { sub?.remove?.(); } catch (_) {}
    };
  }, [session?.id, session?.userId, session?.username]);
  const [preSyncing, setPreSyncing] = useState(false);
  const [loginReady, setLoginReady] = useState(()=>getInitialLoginReady(navigator.onLine));
  const [preLoginCountdown, setPreLoginCountdown] = useState(BOOT_COUNTDOWN_SECONDS);
  const [preLoginStage, setPreLoginStage] = useState(()=>getInitialPreloginStage(navigator.onLine));
  const [preLoginProgress, setPreLoginProgress] = useState(()=>getInitialPreloginProgress(navigator.onLine));
  const prepRef = useRef(false);
  const bootAttemptRef = useRef(0);
  const { maintInfo, exitMaintenance } = useMaintenanceInfo({ API_URL, API_SECRET, DB, getDeviceId });

  useEffect(() => {
    ensureBootTelemetryState();
    markBootTelemetry("app-mounted");
  }, []);

  useEffect(() => {
    if (session?.id || session?.userId || session?.username) {
      setBootTelemetryIdentity(session, activeFarm);
      markBootTelemetry("session-restored", { role: session?.role || "", farmId: activeFarm?.id || "" });
    }
  }, [session?.id, session?.userId, session?.username]);

  useEffect(() => {
    setBootTelemetryIdentity(session, activeFarm);
  }, [session?.id, session?.userId, session?.username, activeFarm?.id]);

  useEffect(() => {
    if (!session) return;
    flushBootTelemetryToServer(session, { apiSecret: API_SECRET }).catch(() => {});
    const flushNow = () => { flushBootTelemetryToServer(session, { apiSecret: API_SECRET }).catch(() => {}); };
    window.addEventListener("online", flushNow);
    window.addEventListener("focus", flushNow);
    document.addEventListener("visibilitychange", flushNow);
    return () => {
      window.removeEventListener("online", flushNow);
      window.removeEventListener("focus", flushNow);
      document.removeEventListener("visibilitychange", flushNow);
    };
  }, [session?.id, session?.userId, session?.username, session?.name]);

  const preSyncLogin = useMemo(() => createPreSyncLogin({
    env: {
      navigatorObj: navigator,
      windowObj: window,
      CLIENT_VERSION,
    },
    storage: {
      DB,
    },
    config: {
      GLOBAL_SYNC_FRESHNESS_MS,
      GLOBAL_BOOTSTRAP_PRIORITY,
      GLOBAL_BOOTSTRAP_SECONDARY,
      GLOBAL_KEY,
    },
    sync: {
      pullGlobal,
      ensureServerInstall,
      mergeAuthAccounts,
      syncGlobalCollections: (userName, options = {}) => syncGlobalCollections(userName, options, getSyncGlobalDeps()),
    },
    merge: {
      buildCollectionIdentityKey,
      pickPreferredFarmRecord,
      dedupeCollectionForBootstrap,
      collectionItemsReferSameIdentity,
      choosePreferredCollectionItem,
    },
    ui: {
      setPreSyncing,
      setPreLoginStage,
      setPreLoginProgress,
      setLoginReady,
    },
    telemetry: {
      markBootTelemetry,
    },
  }), [
    CLIENT_VERSION,
    setPreSyncing,
    setPreLoginStage,
    setPreLoginProgress,
    setLoginReady,
  ]);

  useEffect(() => {
    if (!loginReady) return;
    markBootTelemetry("login-ready", { online: navigator.onLine, hasSession: !!session });
    if (!navigator.onLine) {
      const record = finalizeBootTelemetry({ session, farm: activeFarm, status: "offline-ready", source: "offline-login-ready", apiSecret: API_SECRET });
      if (record && session) flushBootTelemetryToServer(session, { apiSecret: API_SECRET }).catch(() => {});
    }
  }, [loginReady, session?.id, activeFarm?.id]);

  useEffect(()=>{
    if(session || !navigator.onLine || loginReady){
      setPreLoginCountdown(60);
      if(session || loginReady) setPreLoginProgress(100);
      return;
    }
    const t = setInterval(()=>{
      setPreLoginCountdown(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return ()=>clearInterval(t);
  },[session, loginReady]);

  useEffect(()=>{
    function handleOffline(){
      if(!session){
        prepRef.current=false;
        setPreSyncing(false);
        markBootTelemetry("offline-ready", { source: "event-offline" });
        setPreLoginStage("offline-ready");
        setPreLoginProgress(100);
        setLoginReady(true);
      }
    }
    function handleOnline(){
      if(!session){
        prepRef.current=false;
        markBootTelemetry("version-check-started", { source: "event-online" });
        setPreLoginStage("checking-version");
        setPreLoginProgress(8);
        setLoginReady(false);
      }
    }
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return ()=>{
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  },[session]);

  useEffect(()=>{
    if(session) {
      setLoginReady(true);
      return;
    }

    if(!navigator.onLine){
      setPreSyncing(false);
      setPreLoginStage("offline-ready");
      setPreLoginProgress(100);
      setLoginReady(true);
      return;
    }

    if(prepRef.current) return;
    prepRef.current = true;
    const attemptId = Date.now();
    bootAttemptRef.current = attemptId;
    setLoginReady(false);
    setPreSyncing(false);
    markBootTelemetry("version-check-started", { attemptId });
    setPreLoginStage("checking-version");
    setPreLoginProgress(12);

    (async ()=>{
      let serverVersionPromise = Promise.resolve(null);
      try {
        const cachedServerVersion = String(localStorage.getItem('fz:appVersion:server') || '');
        if (cachedServerVersion && compareVersions(cachedServerVersion, CLIENT_VERSION) > 0) {
          serverVersionPromise = Promise.resolve(cachedServerVersion);
        } else {
          serverVersionPromise = Promise.race([
            fetchServerVersion(),
            new Promise((resolve)=>setTimeout(()=>resolve(null), 2200))
          ]);
        }
      } catch(e) {}

      setPreLoginProgress((prev)=>Math.max(prev, 22));
      markBootTelemetry("version-check-finished");
      await preSyncLogin();

      if (bootAttemptRef.current !== attemptId) return;
      try {
        setLoginReady(true);
      } catch (e) {}

      let serverVersion = null;
      try {
        serverVersion = await serverVersionPromise;
      } catch (e) {}

      if (bootAttemptRef.current !== attemptId) return;

      if (!session && navigator.onLine && serverVersion && compareVersions(String(serverVersion), CLIENT_VERSION) > 0) {
        setPreLoginStage("updating-version");
        setPreLoginProgress(18);
        let shouldReloadForVersion = true;
        try {
          const key = `fz:preLoginVersionReload:${CLIENT_VERSION}:${String(serverVersion)}`;
          const raw = sessionStorage.getItem(key) || "";
          const data = raw ? JSON.parse(raw) : null;
          const now = Date.now();
          const next = (!data || !data.firstAt || now - Number(data.firstAt) > 10 * 60 * 1000)
            ? { firstAt: now, count: 1 }
            : { ...data, count: Number(data.count || 0) + 1 };
          sessionStorage.setItem(key, JSON.stringify(next));
          shouldReloadForVersion = next.count <= 1;
        } catch (e) {}
        if (!shouldReloadForVersion) {
          setPreLoginStage("version-reload-paused");
          setPreLoginProgress(100);
        } else {
          try {
            if (typeof window.__fzPrepareTechnicalRefresh === 'function') {
              await window.__fzPrepareTechnicalRefresh(String(serverVersion));
            }
          } catch (e) {}
          try {
            const current = new URL(window.location.href);
            current.searchParams.set('update', String(Date.now()));
            current.searchParams.set('v', String(serverVersion));
            current.searchParams.set('reload', 'pre-login-version-mismatch');
            window.location.replace(current.pathname + current.search + current.hash);
            return;
          } catch (e) {
            try {
              window.location.href = window.location.pathname + '?update=' + Date.now() + '&v=' + encodeURIComponent(String(serverVersion)) + '&reload=pre-login-version-mismatch';
              return;
            } catch (_) {}
          }
        }
      }

      setTimeout(()=>{
        ensureAdminAccountAvailable("post-login-ready").catch(()=>{});
      }, 1200);
    })().finally(()=>{
      if (bootAttemptRef.current === attemptId) prepRef.current = false;
    });
  },[session]);


  useEffect(() => {
    if (!loginReady) return;
    if (!navigator.onLine) return;
    const shortcutModules = Array.isArray(currentUiPreferences?.shortcutModules) ? currentUiPreferences.shortcutModules : [];
    const contextModules = shortcutModules.slice(0, 4);
    const moduleSequence = buildWarmModuleSequence({
      currentModule: null,
      shortcutModules,
      contextModules,
      farmId: activeFarm?.id || "",
      fallbackModules: ["purchases", "stock", "services", "occurrences", "inspections"],
    });
    const shellTimer = setTimeout(() => {
      markBootTelemetry("shell-warm-fired", { source: session ? "post-login" : "login" });
      requestShellWarmCache(session ? "post-login-shell" : "login-shell").catch(() => {});
      trackWarmUsage(session ? "shell:session" : "shell:login");
    }, session ? 300 : 450);
    const homeTimer = setTimeout(() => {
      markBootTelemetry("home-warm-fired", { source: session ? "post-login" : "login", modules: contextModules });
      requestHomeWarmCache(session ? "post-login-home" : "login-home").catch(() => {});
      trackWarmUsage(session ? "home:session" : "home:login");
      contextModules.forEach((moduleId) => trackWarmUsage(buildHomeWarmUsageKey(moduleId)));
    }, session ? 1100 : 1550);
    const priorityModules = moduleSequence.slice(0, 4);
    const adaptiveModules = moduleSequence.slice(0, 6);
    const priorityTimer = setTimeout(() => {
      markBootTelemetry("priority-warm-fired", { source: session ? "post-login" : "login", modules: priorityModules });
      runControlledPriorityWarm({
        scope: "priority",
        reason: session ? "post-login-priority" : "login-priority",
        farmId: activeFarm?.id || "",
        userName: session?.name || "priority-warm",
        moduleIds: priorityModules,
        freshnessMs: 90000,
        currentModule: null,
        maxModules: 4,
      }, getWarmControlDeps()).catch(() => {});
      priorityModules.forEach((moduleId) => trackWarmUsage(`signal:priority:${moduleId}`));
    }, session ? 2000 : 2600);
    const adaptiveTimer = setTimeout(() => {
      markBootTelemetry("adaptive-warm-fired", { source: session ? "post-login" : "login", modules: adaptiveModules });
      runControlledPriorityWarm({
        scope: "adaptive",
        reason: session ? "post-login-adaptive" : "login-adaptive",
        farmId: activeFarm?.id || "",
        userName: session?.name || "adaptive-warm",
        moduleIds: adaptiveModules,
        freshnessMs: 180000,
        currentModule: null,
        maxModules: 6,
      }, getWarmControlDeps()).catch(() => {});
      adaptiveModules.forEach((moduleId) => trackWarmUsage(`signal:adaptive:${moduleId}`));
      const record = finalizeBootTelemetry({ session, farm: activeFarm, status: navigator.onLine ? "ready" : "offline-ready", source: session ? "post-login-warm" : "login-warm", apiSecret: API_SECRET });
      if (record && session) flushBootTelemetryToServer(session, { apiSecret: API_SECRET }).catch(() => {});
    }, session ? 3600 : 4600);
    return () => {
      clearTimeout(shellTimer);
      clearTimeout(homeTimer);
      clearTimeout(priorityTimer);
      clearTimeout(adaptiveTimer);
    };
  }, [loginReady, session, activeFarm?.id, currentUiPreferences?.shortcutModules]);


  useEffect(()=>{
    if(session?.role!=="solicitante") return;
    if(activeFarm?.id) return;
    setSession(null);
    setActiveFarm(null);
    localStorage.removeItem("fz:session");
    localStorage.removeItem("fz:activeFarm");
  },[session?.role, activeFarm?.id]);

  const performSessionLogout = useCallback(({ confirm = true, reason = "manual" } = {}) => {
    const ok = !confirm || typeof window === "undefined" ? true : window.confirm("Deseja realmente sair do app?");
    if (!ok) return false;
    try {
      sessionStorage.removeItem("fz:update:dismissed");
      sessionStorage.removeItem("fz:update:postponeUntil");
      if (reason === "idle-timeout") safeLocalStorageSetItem("fz:autoLogout:last", new Date().toISOString(), { silent: true });
    } catch (e) {}
    setSession(null);
    setActiveFarm(null);
    localStorage.removeItem("fz:session");
    localStorage.removeItem("fz:activeFarm");
    setTimeout(()=>{
      try {
        vcheck?.checkForUpdates?.({ keepVisible: true, ignoreDismissed: true, forceVisible: true });
      } catch (e) {}
    }, 80);
    return true;
  }, [setSession, setActiveFarm, vcheck]);

  const autoLogoutPullGlobalRef = useRef(pullGlobal);
  const autoLogoutLogoutRef = useRef(performSessionLogout);
  useEffect(()=>{ autoLogoutPullGlobalRef.current = pullGlobal; }, [pullGlobal]);
  useEffect(()=>{ autoLogoutLogoutRef.current = performSessionLogout; }, [performSessionLogout]);

  const autoLogoutSessionKey = session
    ? String(session?.id || session?.userId || session?.username || session?.name || session?.role || "session")
    : "";
  const [farmGenerationLogoutNotice, setFarmGenerationLogoutNotice] = useState(null);

  useEffect(()=>{
    if(!session || !activeFarm?.id || !Array.isArray(farms) || farms.length === 0) return;
    const currentFarmRecord = farms.find(item => String(item?.id || "") === String(activeFarm.id));
    if(!currentFarmRecord) return;
    const knownGeneration = getActiveFarmGeneration(activeFarm);
    const currentGeneration = getActiveFarmGeneration(currentFarmRecord);
    if(currentGeneration > knownGeneration){
      const nowMs = Date.now();
      const deadlineRaw = currentFarmRecord?.generationForceLogoutDeadlineAt || currentFarmRecord?.generationLogoutDeadlineAt || "";
      const delaySeconds = [30,60,180,420,600].includes(Number(currentFarmRecord?.generationForceLogoutDelaySeconds)) ? Number(currentFarmRecord.generationForceLogoutDelaySeconds) : 30;
      const deadlineMs = Number.isFinite(Date.parse(deadlineRaw)) ? Date.parse(deadlineRaw) : nowMs + delaySeconds * 1000;
      const generationEvent = {
        farmId: String(activeFarm.id),
        previousGeneration: knownGeneration,
        activeGeneration: currentGeneration,
        at: new Date().toISOString(),
        deadlineAt: new Date(deadlineMs).toISOString(),
        delaySeconds,
        nativeShell: isNativeShell(),
      };
      try { localStorage.setItem("fz:farmGeneration:lastDetected", JSON.stringify(generationEvent)); } catch (e) {}
      let cancelled = false;
      let tickTimer = null;
      let logoutTimer = null;
      const userName = session?.name || session?.username || session?.role || (isNativeShell() ? "native" : "web");
      const nextActiveFarmRecord = {
        ...(activeFarm || {}),
        ...(currentFarmRecord || {}),
        activeDataGeneration: currentGeneration,
        farmDataGeneration: currentGeneration,
      };
      const updateNotice = () => {
        const remainingSeconds = Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000));
        setFarmGenerationLogoutNotice({
          ...generationEvent,
          remainingSeconds,
          message: currentFarmRecord?.generationForceLogoutMessage || "A fazenda entrou em Nova Geração. Salve alterações em andamento; a saída será forçada em instantes.",
        });
        if (remainingSeconds <= 0 && tickTimer) {
          clearInterval(tickTimer);
          tickTimer = null;
        }
      };
      updateNotice();
      tickTimer = setInterval(updateNotice, 1000);
      try { window.dispatchEvent(new CustomEvent("fz:farm-generation:deadline", { detail: generationEvent })); } catch (_) {}
      try { window.dispatchEvent(new CustomEvent("fz:sync:request-now", { detail: generationEvent })); } catch (_) {}
      logoutTimer = setTimeout(async () => {
        if (cancelled) return;
        try {
          setActiveFarm(nextActiveFarmRecord);
          try { safeLocalStorageSetJson("fz:activeFarm", nextActiveFarmRecord); } catch (_) {}
          try { window.dispatchEvent(new CustomEvent("fz:activeFarm:generation-updated", { detail: { farmId: String(activeFarm.id), activeGeneration: currentGeneration } })); } catch (_) {}
          await endAllLiveActivitiesForFarm(activeFarm.id).catch(() => {});
          await clearLocalStateForFarmGeneration({ farmId: activeFarm.id, farm: nextActiveFarmRecord, userName });
          if (isNativeShell()) {
            await refreshNativeFarmGenerationFromServer({ farm: nextActiveFarmRecord, userName }).catch(() => null);
          }
        } catch (_) {}
        if (cancelled) return;
        try { localStorage.setItem("fz:farmGeneration:lastLogout", JSON.stringify(generationEvent)); } catch (e) {}
        setFarmGenerationLogoutNotice(null);
        performSessionLogout({ confirm:false, reason:"farm-generation-changed" });
      }, Math.max(0, deadlineMs - nowMs));
      return () => {
        cancelled = true;
        if (tickTimer) clearInterval(tickTimer);
        if (logoutTimer) clearTimeout(logoutTimer);
      };
    }
  }, [session, activeFarm?.id, activeFarm?.activeDataGeneration, activeFarm?.farmDataGeneration, farms, performSessionLogout, setActiveFarm, showNativeShellToast]);

  useEffect(()=>{
    if(!session) return undefined;
    if (isNativeShell()) return undefined;
    let cancelled = false;
    let timeoutId = null;
    let watchdogId = null;
    let refreshSettingsTimer = null;
    let settings = readCachedAutoLogoutSettings();
    let timeoutMs = Math.max(1, Number(settings?.timeoutMinutes || AUTO_LOGOUT_DEFAULT_MINUTES)) * 60 * 1000;
    let lastActivityAt = Date.now();
    let loggedOut = false;
    const userName = session?.name || session?.username || autoLogoutSessionKey || "session";

    const clearScheduledTimer = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const runIdleCheck = () => {
      if (cancelled || loggedOut) return;
      const elapsed = Date.now() - lastActivityAt;
      if (elapsed >= timeoutMs) {
        loggedOut = true;
        clearScheduledTimer();
        autoLogoutLogoutRef.current?.({ confirm:false, reason:"idle-timeout" });
        return;
      }
      const delay = Math.max(1000, Math.min(timeoutMs - elapsed, 60000));
      clearScheduledTimer();
      timeoutId = window.setTimeout(runIdleCheck, delay);
    };

    const applySettings = (nextSettings) => {
      settings = nextSettings || settings || readCachedAutoLogoutSettings();
      timeoutMs = Math.max(1, Number(settings?.timeoutMinutes || AUTO_LOGOUT_DEFAULT_MINUTES)) * 60 * 1000;
      runIdleCheck();
    };

    const markActivity = () => {
      if (cancelled || loggedOut) return;
      lastActivityAt = Date.now();
      runIdleCheck();
    };

    const checkWhenTabReturns = () => {
      if (document.visibilityState === "visible") runIdleCheck();
    };

    // Não usamos mousemove/focus puro: em alguns navegadores/dispositivos eles podem disparar
    // ruído sem intenção real do usuário e impedir o timeout indefinidamente.
    const activityEvents = ["mousedown", "keydown", "wheel", "scroll", "touchstart", "pointerdown", "click", "input"];
    activityEvents.forEach(eventName => window.addEventListener(eventName, markActivity, { passive:true, capture:true }));
    document.addEventListener("visibilitychange", checkWhenTabReturns, { passive:true });
    window.addEventListener("focus", runIdleCheck);

    const onSettingsChanged = (event) => applySettings(event?.detail || readCachedAutoLogoutSettings());
    window.addEventListener("fz:autoLogoutSettingsChanged", onSettingsChanged);

    const pullSettings = () => {
      const pullGlobalFn = autoLogoutPullGlobalRef.current;
      return pullAutoLogoutSettings({ pullGlobal: pullGlobalFn, userName }).then(next=>{
        if (!cancelled) applySettings(next);
      }).catch(()=>{});
    };

    pullSettings();
    refreshSettingsTimer = window.setInterval(pullSettings, 60000);

    watchdogId = window.setInterval(runIdleCheck, 15000);
    runIdleCheck();
    return ()=>{
      cancelled = true;
      clearScheduledTimer();
      if (watchdogId) window.clearInterval(watchdogId);
      if (refreshSettingsTimer) window.clearInterval(refreshSettingsTimer);
      activityEvents.forEach(eventName => window.removeEventListener(eventName, markActivity, { capture:true }));
      document.removeEventListener("visibilitychange", checkWhenTabReturns, { capture:false });
      window.removeEventListener("focus", runIdleCheck);
      window.removeEventListener("fz:autoLogoutSettingsChanged", onSettingsChanged);
    };
  }, [autoLogoutSessionKey]);

  function handleLogout(){
    performSessionLogout({ confirm:true, reason:"manual" });
  }

  function handleLogin(sess,farm){
    resetMobileViewportAfterLogin();
    const normalizedSession = { ...sess, uiPreferences: normalizeUiPreferences(sess?.uiPreferences) };
    setBootTelemetryIdentity(normalizedSession, farm);
    markBootTelemetry("user-login", { role: normalizedSession?.role || "", farmId: farm?.id || "" });
    setSession(normalizedSession);
    safeLocalStorageSetJson("fz:session", normalizedSession);
    if(normalizedSession.role==="solicitante"){
      setActiveFarm(farm);
      safeLocalStorageSetJson("fz:activeFarm", farm);
    } else {
      setActiveFarm(null);
      localStorage.removeItem("fz:activeFarm");
    }
  }

  useEffect(()=>{
    if(!session) return;
    resetMobileViewportAfterLogin();
    const t1=setTimeout(resetMobileViewportAfterLogin, 80);
    const t2=setTimeout(resetMobileViewportAfterLogin, 260);
    return ()=>{clearTimeout(t1);clearTimeout(t2);};
  },[session?.id, session?.role, activeFarm?.id]);

  useEffect(()=>{
    const onBeforeUnload = (e)=>{
      try {
        const bypassUntil = Number(window.__fzBypassBeforeUnloadUntil || 0);
        const bypassNow = !!window.__fzBypassBeforeUnload;
        const bootSplashVisible = !!window.__fzBootSplashVisible;
        const technicalUpdate = !!window.__fzTechnicalUpdateInProgress;
        if (bypassNow || technicalUpdate || bootSplashVisible || (bypassUntil && Date.now() <= bypassUntil)) return undefined;
      } catch (_) {}
      const message = "Tem certeza que deseja sair do app? Alterações não salvas podem ser perdidas.";
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    if(typeof window !== "undefined") window.addEventListener("beforeunload", onBeforeUnload);
    return ()=>{ if(typeof window !== "undefined") window.removeEventListener("beforeunload", onBeforeUnload); };
  },[]);

  const preLoginStageLabels = PRELOGIN_STAGE_LABELS;


const appContextValue = useMemo(() => ({
  session,
  setSession,
  activeFarm,
  setActiveFarm,
  farms,
  loginReady,
  setLoginReady,
  preSyncing,
  preLoginStage,
  preLoginProgress,
  displayVersion,
}), [
  session,
  activeFarm,
  farms,
  loginReady,
  preSyncing,
  preLoginStage,
  preLoginProgress,
  displayVersion,
]);

  const loginScreenDeps = React.useMemo(() => buildLoginScreenDeps({
    syncFarmCollaboratorAccountsToEmployees,
    getAccounts,
    deriveAllowedModules,
    normalizeModulePermissions,
    normalizeCostCenterPermissions,
    normalizeUiPreferences,
    ensureDefaultAdminAccount,
    saveAccounts,
    pushGlobal,
    DB,
    ensureAdminAccountAvailable,
    getPersistedAdminCredentialState,
    readAdminPasswordOverride,
    stripDeletedItems,
    isFarmArchived,
    T,
    FarmEmoji,
    PwInput,
    Btn,
    Input,
  }), []);

  const farmDashboardDeps = React.useMemo(() => buildFarmDashboardDeps({
    useDisplayedAppVersion,
    useSync,
    VISIBLE_MODULES,
    canViewModule,
    normalizeUiPreferences,
    makeSeed,
    useFarmCollection,
    useGlobalCollection,
    normalizeAdminGlobalsConfig,
    getDeviceId,
    createClientId,
    NOTIFICATIONS_DEVICE_URL,
    API_SECRET,
    MAPS_PLATFORM,
    normalizeServiceType,
    itemMatchesCostCenterAccess,
    canViewCostCenter,
    warmUsageBaseModuleKey,
    MODULE_PRIORITY_WARM_PLAN,
    trackContextualWarmUsage,
    recordSessionWarmNavigation,
    getSessionWarmCandidates,
    runControlledPriorityWarm,
    getWarmControlDeps,
    trackWarmUsage,
    getPriorityConfig,
    getEditableServiceStatuses,
    SHORTCUT_MODULE_CHOICES,
    getContrastTextColor,
    syncFarmCollaboratorAccountsToEmployees,
    MODULES,
    SVC_STATUS_FLOW,
    ModuleHeaderMenu,
    ServicePhotosModal,
    ServicePhotoStatusIcon,
    ServicePhotoAddPhoneIcon,
    ServiceDetailModal,
    ServiceDenyModal,
    PauseTaskModal,
    NFModal,
    MaterialsModal,
    ColActionButtons,
    OccurrencesModule,
    SystemListsManager,
    ServicesModule,
    roleColor,
    isFarmArchived,
    PurchasesHubModule,
    AnimalsModule,
    AvicultureModule,
    ClimateModule,
    FinancialModule,
    HRModule,
    EquipamentosEPIsModule,
    VetModule,
    StockModule,
    MachineryModule,
    HarasModule,
    LocationHubModule,
    SedeModule,
    InspectionsModule,
    MeuPerfilModule,
    ShortcutMonoIcon,
    getShortcutChoiceMeta,
    getFarmEmojiConfig,
    FarmEmoji,
    SyncStatusBar,
    Modal,
    Badge,
    Btn,
    fmtDate,
    LIGHT_THEME,
    T,
  }), []);

  const syncProviderDeps = React.useMemo(() => buildSyncProviderDeps({
    useSystemLists,
    appendSyncAudit,
    apiCall,
    loadSyncAuditFromStorage,
    saveSyncAuditToStorage,
    getConflictResolutions,
    saveConflictResolutionsToStorage,
    loadConflictResolutionsFromStorage,
    undoConflictResolution,
    SyncQueue,
    DB,
    saveSyncHealthSnapshot,
    formatCollectionLabelSafe,
    loadPendingConflictsFromStorage,
    normalizeConflictListForUI,
    savePendingConflictsToStorage,
    registerBootServiceWorker,
    pickRuntimeMapsKey,
    API_URL,
    API_SECRET,
    getDeviceId,
    MAPS_KEY_URL,
    subscribeOnline,
    syncGlobalCollections: (userName, options = {}) => syncGlobalCollections(userName, options, getSyncGlobalDeps()),
    getSyncGlobalDeps,
    hasRealConnectivity,
    logSyncTelemetry,
    setLiveCollectionCache,
    readLatestCollectionState,
    buildOperationLog,
    stableJson,
    isAppendOnlyCollection,
    refreshCollectionFromServer,
    pullCollection,
    pullCollectionDelta,
    pushCollection,
    pushOrderOps,
    pushOperationalOps,
    checkCollectionRevisions,
    cursorKey,
    verKey,
    shadowKey,
    opsKey,
    pendingKey,
    mergeBootstrapVisibleWithServer,
    mergeProtectedVisibleWithServer,
    mergeAuthAccounts,
    dedupeCollectionForBootstrap,
    pickPreferredFarmRecord,
    choosePreferredCollectionItem,
    mergeAppendOnlyRaw,
    buildLocalRawForSync,
    mergeCollectionsThreeWay,
    listCurrentEditingPresence,
    buildCollectionIdentityKey,
    classifyConflictSeverity,
    appendSyncHistory,
    ensureDefaultAdminAccount,
    stripDeletedItems,
    FARM_COLLECTIONS,
    FARM_BUNDLE_GROUPS,
    applyOperationLog,
    pullGlobal,
    pushGlobal,
    pullFarmBundle,
    checkDeviceBootstrapState,
    startDeviceBootstrapState,
    completeDeviceBootstrapState,
    failDeviceBootstrapState,
    pruneDeletedAccounts,
    getUserDeletionLog,
    createPendingConflict,
  }), []);

  const gestorPanelDeps = React.useMemo(() => buildGestorPanelDeps({
    isFarmArchived,
    T,
    SyncStatusBar,
    FarmCtx,
    FarmDashboard,
    farmDashboardDeps,
    FarmEmoji,
    fmtDate,
  }), [farmDashboardDeps]);

  const adminDeps = useMemo(() => buildAdminDeps({
    useToast, useFarms, useSync, useSysLists, useGlobalCollection, normalizeAdminGlobalsConfig,
    useDisplayedAppVersion, readLocationSyncDebugSnapshot, stripDeletedItems, isFarmArchived, isDeletedItem,
    normalizeServiceType, T, Btn, Field, Badge, Input, Select, Textarea, Modal, PwInput, StatCard,
    DB, apiCall, SYSTEM_LISTS_DEFAULTS, COST_CENTERS, FARM_EMOJIS, FARM_COLORS,
    USER_DELETION_LOG_KEY, pushCollection, pullGlobal, pushGlobal, pruneDeletedAccounts, persistAdminCredentialState,
    DEFAULT_VISIBLE_FARM_SPECIES, fmtDate, fmtDateTime, createClientId, pickRuntimeMapsKey,
    SyncQueue, collectionLabel, formatCollectionLabelSafe, normalizeUiPreferences,
    buildConflictBusinessRows, describeConflictRecord, formatConflictFieldLabel,
    classifyConflictSeverity, normalizeConflictForUI, normalizeConflictListForUI, getDeviceId, API_SECRET,
    BOOT_TELEMETRY_URL, DEVICE_REGISTRY_URL, ANIMAL_SPECIES, COLLAB_LEVELS, DASHBOARD_CARDS, FarmCtx, FarmDashboard, farmDashboardDeps, FarmEmoji,
    InspectionsModule, MODULES, OccurrencesModule, PERMISSION_ACTIONS, PURCHASE_ITEM_STATUS_META, ProvidersModule,
    PurchasesHubModule, SHORTCUT_MODULE_CHOICES, SVC_STATUS_FLOW, ServicePhotoStatusIcon, ShortcutMonoIcon,
    StockModule, SuppliersModule, SyncStatusBar, SystemListsManager, Toast, UI_FONT_SCALE_OPTIONS,
    VISIBLE_MODULES, buildAllCostCenterPermissions, buildAllModulePermissions,
    buildPersonDisplayName, cursorKey, dedupeEmployeeLinkOptions, deriveAllowedModules, emptyCostCenterPermissionSet,
    emptyPermissionSet, ensureDefaultAdminAccount, flattenModuleCardSections, getModuleCardLibrary,
    getModuleCardSections, getPriorityConfig, getPurchaseItemStatus, getShortcutChoiceMeta, isCompactViewport,
    makeCreateMeta, makeUpdateMeta, normalizeAuthAccount, normalizeCostCenterPermissions, normalizeFarmRecord,
    normalizeFarmSpeciesList, normalizeFarmsCollection, normalizeModulePermissions, opsKey, pendingKey,
    pruneDeletedEmployees, roleColor, setLiveCollectionCache, shadowKey, splitPersonNameParts, verKey, writeAdminPasswordOverride,
    ServicesModule,
  }), [
    useToast, useFarms, useSync, useSysLists, useGlobalCollection, normalizeAdminGlobalsConfig,
    useDisplayedAppVersion, readLocationSyncDebugSnapshot, stripDeletedItems, isFarmArchived, isDeletedItem,
    normalizeServiceType, T, Btn, Field, Badge, Input, Select, Textarea, Modal, PwInput, StatCard,
    DB, apiCall, SYSTEM_LISTS_DEFAULTS, COST_CENTERS, FARM_EMOJIS, FARM_COLORS,
    DEFAULT_VISIBLE_FARM_SPECIES, fmtDate, fmtDateTime, createClientId, pickRuntimeMapsKey,
    SyncQueue, collectionLabel, formatCollectionLabelSafe, normalizeUiPreferences,
    buildConflictBusinessRows, describeConflictRecord, formatConflictFieldLabel,
    classifyConflictSeverity, normalizeConflictForUI, normalizeConflictListForUI, getDeviceId, API_SECRET,
    BOOT_TELEMETRY_URL, DEVICE_REGISTRY_URL, ANIMAL_SPECIES, COLLAB_LEVELS, DASHBOARD_CARDS, FarmCtx, FarmDashboard, farmDashboardDeps, FarmEmoji,
    InspectionsModule, MODULES, OccurrencesModule, PERMISSION_ACTIONS, PURCHASE_ITEM_STATUS_META, ProvidersModule,
    PurchasesHubModule, SHORTCUT_MODULE_CHOICES, SVC_STATUS_FLOW, ServicePhotoStatusIcon, ShortcutMonoIcon,
    StockModule, SuppliersModule, SyncStatusBar, SystemListsManager, Toast, UI_FONT_SCALE_OPTIONS,
    USER_DELETION_LOG_KEY, VISIBLE_MODULES, buildAllCostCenterPermissions, buildAllModulePermissions,
    buildPersonDisplayName, cursorKey, dedupeEmployeeLinkOptions, deriveAllowedModules, emptyCostCenterPermissionSet,
    emptyPermissionSet, ensureDefaultAdminAccount, flattenModuleCardSections, getModuleCardLibrary,
    getModuleCardSections, getPriorityConfig, getPurchaseItemStatus, getShortcutChoiceMeta, isCompactViewport,
    makeCreateMeta, makeUpdateMeta, normalizeAuthAccount, normalizeCostCenterPermissions, normalizeFarmRecord,
    normalizeFarmSpeciesList, normalizeFarmsCollection, normalizeModulePermissions, opsKey, pendingKey,
    pruneDeletedEmployees, roleColor, setLiveCollectionCache, shadowKey, splitPersonNameParts, verKey, writeAdminPasswordOverride,
    ServicesModule,
  ]);


  return(
    <AppShell
      appContextValue={appContextValue}
      session={session}
      currentZoom={currentZoom}
      syncProviderDeps={syncProviderDeps}
    >
      <Toast toast={nativeShellToast}/>
      {farmGenerationLogoutNotice && (
        <div style={{position:"fixed",inset:0,zIndex:99999,background:"rgba(2,6,23,.72)",backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{width:"min(520px,100%)",background:T.card,border:`1px solid ${T.border}`,borderRadius:22,padding:22,boxShadow:"0 24px 80px rgba(0,0,0,.35)",display:"grid",gap:14}}>
            <div style={{fontSize:24,fontWeight:900,color:T.text}}>🧹 Nova Geração iniciada</div>
            <div style={{fontSize:14,color:T.muted,lineHeight:1.6}}>{farmGenerationLogoutNotice.message}</div>
            <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <div style={{fontSize:42,fontWeight:900,color:T.red,lineHeight:1}}>{farmGenerationLogoutNotice.remainingSeconds}s</div>
              <div style={{fontSize:13,color:T.text,fontWeight:700}}>Tempo restante para salvar alterações em andamento.</div>
            </div>
            <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>Novos lançamentos devem ser feitos somente após entrar novamente na fazenda, já na geração atual.</div>
          </div>
        </div>
      )}
      <AppRoutes
        session={session}
        maintInfo={maintInfo}
        exitMaintenance={exitMaintenance}
        vcheck={vcheck}
        preLoginStageLabels={preLoginStageLabels}
        preLoginStage={preLoginStage}
        preSyncing={preSyncing}
        preLoginCountdown={preLoginCountdown}
        preLoginProgress={preLoginProgress}
        CLIENT_VERSION={CLIENT_VERSION}
        navigatorObj={navigator}
        farms={farms}
        handleLogin={handleLogin}
        handleLogout={handleLogout}
        themeRevision={themeRevision}
        setSession={setSession}
        activeFarm={activeFarm}
        FarmCtx={FarmCtx}
        MaintenanceModal={MaintenanceModal}
        VersionUpdateOverlay={VersionUpdateOverlay}
        LoginScreen={LoginScreen}
        loginScreenDeps={loginScreenDeps}
        GestorPanel={GestorPanel}
        gestorPanelDeps={gestorPanelDeps}
        FarmDashboard={FarmDashboard}
        farmDashboardDeps={farmDashboardDeps}
        adminDeps={adminDeps}
      />
    </AppShell>
  );
}

export default function App(){
  return <AppWithSync/>;
}
